"use client";

import { startTransition, useEffect, useMemo, useRef, useState } from "react";
import { createClient } from "@supabase/supabase-js";
import { artworks as mockArtworks, criteria } from "./mockData";

const criteriaHelp = {
  Story:
    "Does this artwork convey a narrative, share a moment, or describe a scene you can imagine happening?",
  Originality:
    "Consider the time and place this was created. Does this artwork feel innovative, surprising, or different from what you usually see?",
  "Historical Context":
    "Does this artwork help you picture when, where, or how people lived?",
  "Visual Impact":
    "How strong or engaging are the visual elements like color, composition, and detail?",
  Purpose:
    "Does this artwork seem to have a function or express a message, belief, or deeper idea?",
  "Overall Impression":
    "How much did this artwork resonate with you or stay in your mind? Did it affect you? Make you feel something?"
};

const DEFAULT_SURVEY_SECTIONS = [
  {
    title: "What counts as art?",
    questions: [
      "Do you think you know art when you see it?",
      "Does something have to be made by an artist to be considered art?",
      "Can something everyday be art?",
      "Do older objects automatically become art over time?",
      "Can something functional also be art?",
      "Does art have to be displayed in a museum to be considered art?"
    ]
  },
  {
    title: "Meaning & Purpose",
    questions: [
      "Is art more interesting when it communicates a clear message?",
      "Is art more interesting when it's provocative?",
      "Should art serve everyone equally?",
      "Does the value of art deepen when it tells a story?",
      "Can art be irresponsible?",
      "Should art sometimes challenge viewers, even if it makes them uncomfortable?"
    ]
  },
  {
    title: "Personal Connection",
    questions: [
      "Do you need to like a piece of art to see its value?",
      "Can art be good even if it makes you uncomfortable?",
      "Does art have to move you emotionally to matter?",
      "Do you usually spend more than a minute looking at a single artwork?",
      "Do you read wall labels at museums?",
      "Have you ever enjoyed art you didn't fully understand?",
      "Do you see yourself, your values, reflected in most art museums?",
      "Are you an artist?"    ]
  },
  {
    title: "Value & Authority",
    questions: [
      "Do experts have a better understanding of art than the general public?",
      "Should museums decide what counts as important art?",
      "Would you want a say in the curation of your local art museum?",
      "Does fame make an artwork more valuable?",
      "Does knowing the history of an artwork change its value?",
      "Should art lose value if it looks like other art?",
      "Do you think money influences which artworks are considered important?"
    ]
  },
  {
    title: "Boundaries of Art",
    questions: [
      "Can an idea alone be art, even without a physical object?",
      "Can digital or AI-generated work be art?",
      "Can a performance or moment in time be art?",
      "Can something accidental be art?",
      "Does art need to be original or innovative?",
      "Can copying or reusing ideas still be art?"
    ]
  }
];

const DEFAULT_SURVEY_QUESTIONS = DEFAULT_SURVEY_SECTIONS.flatMap((section) => section.questions);

const ratingHeaders = [
  "Not an expert? Go ahead, rate this artwork.",
  "Not an expert? Go ahead, rate this artwork.",
  "No art degree required, your perspective matters.",
  "Go ahead, have thoughts.",
  "Art isn't just for experts. What do you think?",
  "Curators have their perspective. What's yours?",
  "Does this artwork challenge you?",
  "Your turn. How would you rate this artwork?",
  "How does age affect your concept of art?",
  "Does this artwork feel meaningful to you?",
  "Does this artwork have something to say?",
  "Does this piece raise questions for you?",
  "Does this artwork evoke an emotional response in you?",
  "Can you relate to this artwork, personally?",
  "Does this artwork hold your attention?",
  "Does anything about this artwork feel confusing or unclear?",
  "Do you wish you had more explanation about this artwork?",
  "Does this artwork make you want to know more?",
  "Does this artwork make you think differently about a topic or issue?",
  "Do you feel represented in some way by this artwork?",
  "Does your reaction to this artwork surprise you?",
  "Does this artwork challenge you?",
  "Would you want to talk about this artwork with someone else?",
  "Art or not? You tell us.",
  "Does this artwork make you want to know more?"
];

const HAS_SUPABASE = Boolean(process.env.NEXT_PUBLIC_SUPABASE_URL);
const USE_SUPABASE_CATALOG = HAS_SUPABASE;
const IMAGE_PROXY_ENABLED = false;
const IMAGE_CDN_QUALITY = 70;
const IMAGE_CDN_ENABLED =
  (typeof window !== "undefined" &&
    !["localhost", "127.0.0.1"].includes(window.location.hostname)) ||
  process.env.NEXT_PUBLIC_IMAGE_CDN === "true";
const OBJECT_CACHE_VERSION = "2026-02-01-ratings-summary-v2";
const SEEN_ARTWORKS_KEY = "ratedArtSeenArtworks";
const SEEN_ARTWORKS_LIMIT = 2000;
const OBJECT_BATCH_SIZE = 24;
const OBJECT_FETCH_RETRIES = 3;
const OBJECT_FETCH_BACKOFF_MS = 400;
const PENDING_EVENTS_KEY = "ratedArtPendingEvents";
const MAX_PENDING_EVENTS = 100;
const ID_POOL_CACHE_KEY = "ratedArtIdPool";
const ID_POOL_CACHE_VERSION = "catalog-v1";
const GLOBAL_RATED_KEY = "ratedArtGlobalRatedArtworks";
const OPEN_ACCESS_TOTAL_KEY = "ratedArtOpenAccessTotal";
const ID_POOL_TARGET = 2500;
const ID_POOL_BATCH = 400;
const ID_POOL_MAX = 8000;
const READY_POOL_TARGET = 160;
const SURVEY_PREFETCH_TARGET = 48;
const INITIAL_PREFETCH_TARGET = 24;
const RATING_PREFETCH_TARGET = 12;
const READY_POOL_THRESHOLD = 60;
const DETAILS_BATCH_SIZE = 50;
const NAV_THROTTLE_MS = 350;
const GLOBAL_RATINGS_CACHE_TTL = 1000 * 60;


const collectionSearchFields = [
  "All Fields",
  "Artist / Culture",
  "Title",
  "Description",
  "Gallery",
  "Object Number",
  "Credit Line"
];

function getSearchPlaceholder(field) {
  if (!field || field === "All Fields") return "Search all fields";
  return `Search ${field.toLowerCase()}`;
}

const CRITERIA_WEIGHTS = {
  Story: 0.15,
  Originality: 0.15,
  "Historical Context": 0.15,
  "Visual Impact": 0.2,
  Purpose: 0.15,
  "Overall Impression": 0.2
};

const ENABLE_COMPARE = false;
const MET_SAMPLER_QUERY = "portrait";
const COLLECTION_DEFAULT_QUERY = "art";
const USE_SERVER_ROUTES = true;
const API_BASE_URL =
  process.env.NEXT_PUBLIC_API_BASE_URL ||
  (typeof window !== "undefined" ? window.location.origin : "http://localhost");

function resolveApiUrl(path, params) {
  if (/^https?:\/\//i.test(path)) return path;
  const apiUrl = new URL(API_BASE_URL);
  apiUrl.pathname = path;
  Object.entries(params || {}).forEach(([key, value]) => {
    if (value === undefined || value === null || value === "") return;
    apiUrl.searchParams.set(key, String(value));
  });
  return apiUrl.toString();
}

function sum(values) {
  return values.reduce((acc, value) => acc + value, 0);
}

function normalizeSortText(value) {
  return (value || "").toString().trim().toLowerCase();
}

function compareText(a, b) {
  return normalizeSortText(a).localeCompare(normalizeSortText(b));
}

function compareNumbers(a, b, direction = "asc") {
  const left = Number.isFinite(a) ? a : null;
  const right = Number.isFinite(b) ? b : null;
  if (left === null && right === null) return 0;
  if (left === null) return 1;
  if (right === null) return -1;
  return direction === "desc" ? right - left : left - right;
}

function parseDateYear(value) {
  if (!value) return null;
  const text = value.toString().toLowerCase();
  const centuryMatch = text.match(/(\d{1,2})(st|nd|rd|th)\s+century/);
  if (centuryMatch) {
    const century = Number(centuryMatch[1]);
    if (Number.isFinite(century)) {
      return (century - 1) * 100;
    }
  }
  const match = text.match(/-?\d{1,4}/);
  if (!match) return null;
  const year = Number(match[0]);
  if (!Number.isFinite(year)) return null;
  const isBce = /bce|bc/.test(text);
  return isBce ? -Math.abs(year) : year;
}

function slugifySectionId(value) {
  return value
    .toString()
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "");
}

function buildCollectionQueryTerm(query) {
  const parts = [];
  if (query && query !== COLLECTION_DEFAULT_QUERY) parts.push(query.trim());
  return parts.length ? parts.join(" ") : COLLECTION_DEFAULT_QUERY;
}

function buildEmptyDistribution() {
  return criteria.reduce((acc, key) => {
    acc[key] = [0, 0, 0, 0, 0];
    return acc;
  }, {});
}

function cloneDistribution(distribution) {
  return criteria.reduce((acc, key) => {
    const values = distribution?.[key] || [0, 0, 0, 0, 0];
    acc[key] = [...values];
    return acc;
  }, {});
}

function subtractDistribution(base, toSubtract) {
  return criteria.reduce((acc, key) => {
    const values = base?.[key] || [0, 0, 0, 0, 0];
    const remove = toSubtract?.[key] || [0, 0, 0, 0, 0];
    acc[key] = values.map((value, index) => Math.max(0, value - (remove[index] || 0)));
    return acc;
  }, {});
}

function computeAverage(distribution) {
  const totalVotes = sum(distribution);
  if (!totalVotes) return 0;
  const weighted = distribution.reduce(
    (acc, count, index) => acc + count * (index + 1),
    0
  );
  return weighted / totalVotes;
}

function computeOverallAverage(artwork) {
  const averages = criteria.map((criterion) =>
    computeAverage(artwork.distribution[criterion])
  );
  const total = averages.reduce((acc, value) => acc + value, 0);
  return total / averages.length;
}

function getRatingDistribution(artwork) {
  if (artwork?.__summaryDistribution) return artwork.__summaryDistribution;
  return artwork?.distribution;
}

function computeOverallAverageFromDistribution(distribution) {
  if (!distribution) return 0;
  const averages = criteria.map((criterion) =>
    computeAverage(distribution[criterion] || [0, 0, 0, 0, 0])
  );
  const total = averages.reduce((acc, value) => acc + value, 0);
  return total / averages.length;
}

function computeOverallAverageFromDistributionSparse(distribution) {
  if (!distribution) return 0;
  const averages = criteria
    .map((criterion) => {
      const dist = distribution[criterion] || [0, 0, 0, 0, 0];
      const total = sum(dist);
      if (!total) return null;
      return computeAverage(dist);
    })
    .filter((value) => Number.isFinite(value));
  if (!averages.length) return 0;
  return averages.reduce((acc, value) => acc + value, 0) / averages.length;
}

function computeOverallAverageForItem(artwork) {
  const distribution = getRatingDistribution(artwork);
  return computeOverallAverageFromDistributionSparse(distribution);
}

function buildDistributionFromScores(scores) {
  const distribution = {};
  criteria.forEach((criterion) => {
    const value = Number(scores?.[criterion]);
    if (!Number.isFinite(value) || value < 1 || value > 5) return;
    if (!distribution[criterion]) distribution[criterion] = [0, 0, 0, 0, 0];
    distribution[criterion][value - 1] += 1;
  });
  return distribution;
}

function dedupeCollectionItems(items) {
  const seen = new Map();
  items.forEach((item) => {
    if (!item) return;
    const imageKey = item.imageSmall || item.imageLarge || "";
    const objectKey = item.objectNumber || item.accessionNumber || "";
    let key = "";
    if (imageKey) {
      key = `img:${imageKey}`;
    } else if (objectKey) {
      key = `obj:${objectKey}`;
    } else if (item.id) {
      key = `id:${item.id}`;
    } else {
      const titleKey = item.title || "";
      const artistKey = item.artist || "";
      key = `meta:${titleKey}|${artistKey}`;
    }
    if (!seen.has(key)) {
      seen.set(key, item);
    }
  });
  return Array.from(seen.values());
}

function shuffleArray(items) {
  const next = [...items];
  for (let i = next.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    [next[i], next[j]] = [next[j], next[i]];
  }
  return next;
}

function formatAverage(artwork) {
  if (artwork.displayScore) {
    const score = artwork.displayScore > 5 ? artwork.displayScore / 2 : artwork.displayScore;
    return score.toFixed(1);
  }
  const value = computeOverallAverage(artwork);
  if (!value) return "–";
  return value.toFixed(1);
}

function computeDistributionStats(distribution) {
  const total = sum(distribution);
  if (!total) {
    return { total: 0, mean: 0, variance: 0 };
  }
  const mean = distribution.reduce(
    (acc, count, index) => acc + count * (index + 1),
    0
  ) / total;
  const variance =
    distribution.reduce((acc, count, index) => {
      const score = index + 1;
      const diff = score - mean;
      return acc + count * diff * diff;
    }, 0) / total;
  return { total, mean, variance };
}

function dedupeIds(values) {
  return Array.from(new Set(values));
}

function sleep(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

function normalizeId(value) {
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : null;
}

const MET_SEARCH_URL =
  "https://collectionapi.metmuseum.org/public/collection/v1/search";
const MET_OBJECT_URL =
  "https://collectionapi.metmuseum.org/public/collection/v1/objects/";

function buildMetSearchUrl({
  q,
  limit,
  offset,
  departmentId,
  geoLocation,
  medium,
  dateBegin,
  dateEnd
}) {
  const apiUrl = new URL(MET_SEARCH_URL);
  apiUrl.searchParams.set("hasImages", "true");
  apiUrl.searchParams.set("isPublicDomain", "true");
  apiUrl.searchParams.set("q", q || "a");
  if (typeof limit === "number") apiUrl.searchParams.set("limit", String(limit));
  if (typeof offset === "number") apiUrl.searchParams.set("offset", String(offset));
  if (departmentId) apiUrl.searchParams.set("departmentId", departmentId);
  if (geoLocation) apiUrl.searchParams.set("geoLocation", geoLocation);
  if (medium) apiUrl.searchParams.set("medium", medium);
  if (dateBegin) apiUrl.searchParams.set("dateBegin", dateBegin);
  if (dateEnd) apiUrl.searchParams.set("dateEnd", dateEnd);
  return apiUrl;
}

async function fetchMetSearchDirect(params) {
  try {
    const apiUrl = buildMetSearchUrl(params);
    const response = await fetch(apiUrl.toString(), { cache: "no-store" });
    if (!response.ok) return { total: 0, objectIDs: [] };
    const data = await response.json();
    return {
      total: Number(data.total || 0),
      objectIDs: Array.isArray(data.objectIDs) ? data.objectIDs : []
    };
  } catch {
    return { total: 0, objectIDs: [] };
  }
}

async function fetchSearchServer(params) {
  try {
    const apiUrl = new URL(API_BASE_URL);
    apiUrl.pathname = "/api/search";
    apiUrl.searchParams.set("q", params.q || MET_SAMPLER_QUERY);
    apiUrl.searchParams.set("limit", String(params.limit || 24));
    apiUrl.searchParams.set("offset", String(params.offset || 0));
    if (params.random) apiUrl.searchParams.set("random", "true");
    if (params.departmentId) apiUrl.searchParams.set("departmentId", params.departmentId);
    if (params.geoLocation) apiUrl.searchParams.set("geoLocation", params.geoLocation);
    if (params.medium) apiUrl.searchParams.set("medium", params.medium);
    if (params.dateBegin) apiUrl.searchParams.set("dateBegin", params.dateBegin);
    if (params.dateEnd) apiUrl.searchParams.set("dateEnd", params.dateEnd);
    const response = await fetch(apiUrl.toString(), { cache: "no-store" });
    if (!response.ok) return { total: 0, objectIDs: [] };
    const data = await response.json();
    return {
      total: Number(data.total || 0),
      objectIDs: Array.isArray(data.objectIDs) ? data.objectIDs : []
    };
  } catch {
    return { total: 0, objectIDs: [] };
  }
}

async function fetchCatalogPage(params) {
  try {
    const apiUrl = resolveApiUrl("/api/catalog/search", params);
    const response = await fetch(apiUrl, { cache: "no-store" });
    if (!response.ok) {
      return { total: 0, items: [], ok: false, status: response.status };
    }
    const data = await response.json();
    return {
      total: Number(data.total || 0),
      items: Array.isArray(data.items) ? data.items : [],
      ok: true,
      status: response.status
    };
  } catch {
    return { total: 0, items: [], ok: false, status: 0 };
  }
}

async function fetchMetObjectsDirect(ids, maxConcurrency = 6) {
  if (!ids.length) return [];
  const results = [];
  let index = 0;

  async function worker() {
    while (index < ids.length) {
      const id = ids[index];
      index += 1;
      try {
        const response = await fetch(`${MET_OBJECT_URL}${id}`, { cache: "no-store" });
        if (!response.ok) continue;
        const data = await response.json();
        if (!data.isPublicDomain || (!data.primaryImage && !data.primaryImageSmall)) continue;
        const dateBegin = Number.isFinite(Number(data.objectBeginDate))
          ? Number(data.objectBeginDate)
          : null;
        const dateEnd = Number.isFinite(Number(data.objectEndDate))
          ? Number(data.objectEndDate)
          : null;
        results.push({
          id: data.objectID,
          title: data.title,
          artist: data.artistDisplayName || "Unknown",
          date: data.objectDate || "",
          dateBegin,
          dateEnd,
          medium: data.medium || "",
          objectName: data.objectName || "",
          culture: data.culture || "",
          creditLine: data.creditLine || "",
          accessionNumber: data.accessionNumber || "",
          objectNumber: data.objectNumber || "",
          period: data.period || "",
          classification: data.classification || "",
          artistDisplayBio: data.artistDisplayBio || "",
          description: data.creditLine || data.objectName || "",
          imageSmall: data.primaryImageSmall || data.primaryImage,
          imageLarge: data.primaryImage || data.primaryImageSmall
        });
      } catch {
        // ignore individual failures
      }
    }
  }

  await Promise.all(
    Array.from({ length: Math.min(maxConcurrency, ids.length) }, worker)
  );
  return results;
}

async function fetchMetSearchHybrid(params) {
  const primary = USE_SERVER_ROUTES ? fetchSearchServer : fetchMetSearchDirect;
  const fallback = USE_SERVER_ROUTES ? fetchMetSearchDirect : fetchSearchServer;
  let result = await primary(params);
  const primaryIds = Array.isArray(result.objectIDs) ? result.objectIDs : [];
  const target = typeof params.limit === "number" ? params.limit : 0;
  if (typeof window !== "undefined" && USE_SERVER_ROUTES) {
    return result;
  }
  if (!primaryIds.length || (target && primaryIds.length < target)) {
    const fallbackResult = await fallback(params);
    const fallbackIds = Array.isArray(fallbackResult.objectIDs)
      ? fallbackResult.objectIDs
      : [];
    if (fallbackIds.length) {
      const merged = Array.from(new Set([...primaryIds, ...fallbackIds]));
      return {
        total: Math.max(Number(result.total || 0), Number(fallbackResult.total || 0)),
        objectIDs: target ? merged.slice(0, target) : merged
      };
    }
  }
  return result;
}

async function fetchMetObjectsHybrid(ids, maxConcurrency = 6) {
  if (!ids.length) return [];
  const primary = USE_SERVER_ROUTES ? fetchArtworksServer : fetchMetObjectsDirect;
  const fallback = USE_SERVER_ROUTES ? fetchMetObjectsDirect : fetchArtworksServer;
  let result = await primary(ids, maxConcurrency);
  const minExpected = Math.min(ids.length, 8);
  if (typeof window !== "undefined" && USE_SERVER_ROUTES) {
    return result;
  }
  if (result.length < minExpected) {
    const fallbackResult = await fallback(ids, maxConcurrency);
    if (fallbackResult.length) {
      const merged = new Map();
      result.forEach((item) => merged.set(item.id, item));
      fallbackResult.forEach((item) => merged.set(item.id, item));
      return Array.from(merged.values());
    }
  }

  return result;
}

async function fetchMetObjectsWithRetries(ids, options = {}) {
  const {
    batchSize = OBJECT_BATCH_SIZE,
    concurrency = 6,
    retries = OBJECT_FETCH_RETRIES,
    backoffMs = OBJECT_FETCH_BACKOFF_MS,
    onProgress
  } = options;
  if (!ids.length) return [];
  const results = [];
  for (let i = 0; i < ids.length; i += batchSize) {
    const batch = ids.slice(i, i + batchSize);
    let attempt = 0;
    let fetched = [];
    while (attempt < retries) {
      fetched = await fetchMetObjectsHybrid(batch, concurrency);
      if (fetched.length || attempt === retries - 1) break;
      await sleep(backoffMs * (attempt + 1));
      attempt += 1;
    }
    results.push(...fetched);
    if (onProgress) onProgress(results.length);
  }
  return results;
}

async function fetchArtworksServer(ids) {
  if (!ids.length) return [];
  try {
    const apiUrl = new URL(API_BASE_URL);
    apiUrl.pathname = "/api/artworks";
    apiUrl.searchParams.set("ids", ids.join(","));
    const response = await fetch(apiUrl.toString(), { cache: "no-store" });
    if (!response.ok) return [];
    const data = await response.json();
    return Array.isArray(data.items) ? data.items : [];
  } catch {
    return [];
  }
}

function loadJson(key, fallback) {
  if (typeof window === "undefined") return fallback;
  try {
    const value = localStorage.getItem(key);
    if (!value) return fallback;
    return JSON.parse(value);
  } catch {
    return fallback;
  }
}

function saveJson(key, value) {
  if (typeof window === "undefined") return;
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch (error) {
    if (error?.name !== "QuotaExceededError") throw error;
    try {
      localStorage.removeItem("ratedArtObjectCache");
      localStorage.setItem(key, JSON.stringify(value));
      return;
    } catch {
      // fall through to trimming
    }
    if (Array.isArray(value)) {
      const trimmed = value.slice(-500);
      try {
        localStorage.setItem(key, JSON.stringify(trimmed));
        return;
      } catch {
        // fall through to clearing
      }
    }
    try {
      localStorage.removeItem(key);
    } catch {
      // ignore
    }
  }
}

function getClientId() {
  if (typeof window === "undefined") return null;
  const key = "ratedArtClientId";
  let id = localStorage.getItem(key);
  if (!id) {
    id =
      typeof crypto !== "undefined" && crypto.randomUUID
        ? crypto.randomUUID()
        : `${Date.now()}-${Math.random().toString(16).slice(2)}`;
    localStorage.setItem(key, id);
  }
  return id;
}

function loadPendingEvents() {
  return loadJson(PENDING_EVENTS_KEY, []);
}

function savePendingEvents(events) {
  saveJson(PENDING_EVENTS_KEY, events);
}

function enqueuePendingEvent(entry) {
  const events = loadPendingEvents();
  const next = [...events, entry].slice(-MAX_PENDING_EVENTS);
  savePendingEvents(next);
}

async function postEvent(path, payload) {
  if (!HAS_SUPABASE) return false;
  try {
    const response = await fetch(resolveApiUrl(path), {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      keepalive: true,
      body: JSON.stringify(payload)
    });
    if (!response.ok) {
      enqueuePendingEvent({
        path,
        payload,
        attempts: 1,
        createdAt: new Date().toISOString()
      });
      return { ok: false };
    }
    const data = await response.json().catch(() => ({}));
    return { ok: true, data };
  } catch {
    enqueuePendingEvent({
      path,
      payload,
      attempts: 1,
      createdAt: new Date().toISOString()
    });
    return { ok: false };
  }
}

async function flushPendingEvents() {
  if (!HAS_SUPABASE) return;
  const events = loadPendingEvents();
  if (!events.length) return;
  const remaining = [];
  for (const entry of events) {
    try {
      const response = await fetch(entry.path, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        keepalive: true,
        body: JSON.stringify(entry.payload)
      });
      if (!response.ok) {
        const attempts = (entry.attempts || 0) + 1;
        if (attempts < 3) {
          remaining.push({ ...entry, attempts });
        }
      }
    } catch {
      const attempts = (entry.attempts || 0) + 1;
      if (attempts < 3) {
        remaining.push({ ...entry, attempts });
      }
    }
  }
  savePendingEvents(remaining);
}

function getSessionCount(key) {
  if (typeof window === "undefined") return 0;
  return Number(sessionStorage.getItem(key) || 0);
}

function setSessionCount(key, value) {
  if (typeof window === "undefined") return;
  sessionStorage.setItem(key, String(value));
}

function runWhenIdle(task) {
  if (typeof window === "undefined") return;
  if (typeof window.requestIdleCallback === "function") {
    window.requestIdleCallback(() => task(), { timeout: 1000 });
    return;
  }
  setTimeout(task, 0);
}

function getSupabaseBrowserClient() {
  if (typeof window === "undefined") return null;
  if (window.__supabaseBrowserClient) return window.__supabaseBrowserClient;
  const url = process.env.NEXT_PUBLIC_SUPABASE_URL;
  const anonKey = process.env.NEXT_PUBLIC_SUPABASE_ANON_KEY;
  if (!url || !anonKey) return null;
  window.__supabaseBrowserClient = createClient(url, anonKey, {
    auth: { persistSession: false }
  });
  return window.__supabaseBrowserClient;
}

function getSupabaseRestConfig() {
  const url = process.env.NEXT_PUBLIC_SUPABASE_URL;
  const anonKey = process.env.NEXT_PUBLIC_SUPABASE_ANON_KEY;
  if (!url || !anonKey) return null;
  return { url, anonKey };
}

function getRatingsSummaryWorker() {
  if (typeof window === "undefined") return null;
  if (window.__ratingsSummaryWorker) return window.__ratingsSummaryWorker;
  try {
    window.__ratingsSummaryWorker = new Worker(
      new URL("./workers/ratingsSummaryWorker.js", import.meta.url)
    );
    return window.__ratingsSummaryWorker;
  } catch (error) {
    console.warn("Ratings summary worker unavailable.", error);
    return null;
  }
}

function getNormalizeArtworksWorker() {
  if (typeof window === "undefined") return null;
  if (window.__normalizeArtworksWorker) return window.__normalizeArtworksWorker;
  try {
    window.__normalizeArtworksWorker = new Worker(
      new URL("./workers/normalizeArtworksWorker.js", import.meta.url)
    );
    return window.__normalizeArtworksWorker;
  } catch (error) {
    console.warn("Normalize artworks worker unavailable.", error);
    return null;
  }
}

function mergeDistributionEntries(map, entries) {
  entries.forEach(([key, distribution]) => {
    if (!map.has(key)) {
      map.set(key, distribution);
      return;
    }
    const target = map.get(key);
    Object.entries(distribution || {}).forEach(([criterion, scores]) => {
      if (!target[criterion]) target[criterion] = [0, 0, 0, 0, 0];
      const bucket = target[criterion];
      for (let i = 0; i < 5; i += 1) {
        bucket[i] += Number(scores?.[i] || 0);
      }
    });
  });
}

async function normalizeRatingsRowsWithWorker(rows) {
  const worker = getRatingsSummaryWorker();
  if (!worker || !Array.isArray(rows) || !rows.length) return null;
  return new Promise((resolve) => {
    const requestId = `${Date.now()}-${Math.random()}`;
    const handleMessage = (event) => {
      if (event.data?.id !== requestId) return;
      worker.removeEventListener("message", handleMessage);
      worker.removeEventListener("error", handleError);
      resolve(Array.isArray(event.data?.entries) ? event.data.entries : null);
    };
    const handleError = () => {
      worker.removeEventListener("message", handleMessage);
      worker.removeEventListener("error", handleError);
      resolve(null);
    };
    worker.addEventListener("message", handleMessage);
    worker.addEventListener("error", handleError);
    worker.postMessage({ id: requestId, rows });
  });
}

async function normalizeArtworksWithWorker(items) {
  const worker = getNormalizeArtworksWorker();
  if (!worker || !Array.isArray(items) || !items.length) return null;
  return new Promise((resolve) => {
    const requestId = `${Date.now()}-${Math.random()}`;
    const handleMessage = (event) => {
      if (event.data?.id !== requestId) return;
      worker.removeEventListener("message", handleMessage);
      worker.removeEventListener("error", handleError);
      resolve(Array.isArray(event.data?.items) ? event.data.items : null);
    };
    const handleError = () => {
      worker.removeEventListener("message", handleMessage);
      worker.removeEventListener("error", handleError);
      resolve(null);
    };
    worker.addEventListener("message", handleMessage);
    worker.addEventListener("error", handleError);
    worker.postMessage({ id: requestId, items, criteria });
  });
}

async function normalizeArtworks(items) {
  const workerItems = await normalizeArtworksWithWorker(items);
  if (workerItems) return workerItems;
  return items.map((item) => {
    const baseDistribution = buildEmptyDistribution();
    return {
      id: item.id,
      title: item.title || "Untitled",
      artist: item.artist || "Unknown",
      date: item.date || "",
      dateBegin: Number.isFinite(item.dateBegin)
        ? item.dateBegin
        : Number.isFinite(item.objectBeginDate)
          ? item.objectBeginDate
          : null,
      dateEnd: Number.isFinite(item.dateEnd)
        ? item.dateEnd
        : Number.isFinite(item.objectEndDate)
          ? item.objectEndDate
          : null,
      medium: item.medium || "",
      objectName: item.objectName || "",
      culture: item.culture || "",
      creditLine: item.creditLine || "",
      accessionNumber: item.accessionNumber || "",
      objectNumber: item.objectNumber || "",
      period: item.period || "",
      classification: item.classification || "",
      artistDisplayBio: item.artistDisplayBio || "",
      description: item.description || "",
      imageSmall: item.imageSmall || item.imageLarge,
      imageLarge: item.imageLarge || item.imageSmall,
      distribution: buildEmptyDistribution(),
      __baseDistribution: baseDistribution
    };
  });
}

async function fetchGlobalRatingsSummaryAll(localRatings = null) {
  if (typeof window === "undefined") return new Map();
  if (!window.__globalRatingsCache) {
    window.__globalRatingsCache = { map: null, fetchedAt: 0, inflight: null };
  }
  const cache = window.__globalRatingsCache;
  if (cache.map && cache.map.size > 0 && Date.now() - cache.fetchedAt < GLOBAL_RATINGS_CACHE_TTL) {
    return cache.map;
  }
  if (cache.inflight) return cache.inflight;
  const buildLocalFallback = () => {
    const map = new Map();
    if (localRatings) {
      Object.entries(localRatings).forEach(([id, scores]) => {
        const distribution = buildDistributionFromScores(scores);
        if (Object.keys(distribution).length) {
          map.set(String(id), distribution);
        }
      });
    }
    cache.map = map;
    cache.fetchedAt = Date.now();
    return map;
  };
  cache.inflight = (async () => {
    try {
      const map = new Map();
      const supabase = getSupabaseBrowserClient();
      if (supabase) {
        const { data: rpcData, error: rpcError } = await supabase.rpc(
          "get_ratings_summary_all"
        );
        const rows = !rpcError && Array.isArray(rpcData) ? rpcData : [];
        const normalizeRows = async (inputRows) => {
          const workerEntries = await normalizeRatingsRowsWithWorker(inputRows);
          if (workerEntries) {
            mergeDistributionEntries(map, workerEntries);
            return;
          }
          for (let i = 0; i < inputRows.length; i += 1) {
            const row = inputRows[i];
            const key = String(row.artwork_id);
            if (!map.has(key)) map.set(key, {});
            const target = map.get(key);
            Object.entries(row.scores || {}).forEach(([criterion, value]) => {
              const index = Number(value) - 1;
              if (!Number.isFinite(index) || index < 0 || index > 4) return;
              if (!target[criterion]) target[criterion] = [0, 0, 0, 0, 0];
              target[criterion][index] += 1;
            });
            if (i && i % 250 === 0) {
              await new Promise((resolve) => setTimeout(resolve, 0));
            }
          }
        };
        if (rows.length) {
          await normalizeRows(rows);
        } else {
          const pageSize = 1000;
          let offset = 0;
          while (true) {
            const { data, error } = await supabase
              .from("artwork_ratings")
              .select("artwork_id, scores")
              .range(offset, offset + pageSize - 1);
            if (error || !Array.isArray(data) || !data.length) break;
            await normalizeRows(data);
            if (data.length < pageSize) break;
            offset += pageSize;
          }
        }
      }
      if (!map.size) {
        const rest = getSupabaseRestConfig();
        if (rest) {
          const pageSize = 1000;
          let offset = 0;
          while (true) {
            const response = await fetch(
              `${rest.url}/rest/v1/artwork_ratings?select=artwork_id,scores&limit=${pageSize}&offset=${offset}`,
              {
                headers: {
                  apikey: rest.anonKey,
                  Authorization: `Bearer ${rest.anonKey}`
                },
                cache: "no-store"
              }
            );
            if (!response.ok) break;
            const data = await response.json();
            if (!Array.isArray(data) || !data.length) break;
            const workerEntries = await normalizeRatingsRowsWithWorker(data);
            if (workerEntries) {
              mergeDistributionEntries(map, workerEntries);
            } else {
              for (let i = 0; i < data.length; i += 1) {
                const row = data[i];
                const key = String(row.artwork_id);
                if (!map.has(key)) map.set(key, {});
                const target = map.get(key);
                Object.entries(row.scores || {}).forEach(([criterion, value]) => {
                  const index = Number(value) - 1;
                  if (!Number.isFinite(index) || index < 0 || index > 4) return;
                  if (!target[criterion]) target[criterion] = [0, 0, 0, 0, 0];
                  target[criterion][index] += 1;
                });
                if (i && i % 250 === 0) {
                  await new Promise((resolve) => setTimeout(resolve, 0));
                }
              }
            }
            if (data.length < pageSize) break;
            offset += pageSize;
            await new Promise((resolve) => setTimeout(resolve, 0));
          }
        }
      }
      if (!map.size) {
        const response = await fetch(
          resolveApiUrl("/api/ratings", { summary: "all", ts: Date.now() }),
          { cache: "no-store" }
        );
        if (!response.ok) return buildLocalFallback();
        const data = await response.json();
        (data.items || []).forEach((item) => {
          map.set(String(item.artworkId), item.distribution || {});
        });
      }
      if (localRatings) {
        Object.entries(localRatings).forEach(([id, scores]) => {
          const key = String(id);
          if (map.has(key)) return;
          const distribution = buildDistributionFromScores(scores);
          if (Object.keys(distribution).length) {
            map.set(key, distribution);
          }
        });
      }
      cache.map = map;
      cache.fetchedAt = Date.now();
      return map;
    } catch {
      return buildLocalFallback();
    } finally {
      cache.inflight = null;
    }
  })();
  return cache.inflight;
}

function getSurveyState() {
  if (typeof window === "undefined") {
    return { ratingCount: 0, viewCount: 0, seen: [], responses: [] };
  }
  const ratingCount = Number(localStorage.getItem("ratedArtRatingCount") || 0);
  const viewCount = Number(localStorage.getItem("ratedArtViewCount") || 0);
  const seen = loadJson("ratedArtSeenQuestions", []);
  const responses = loadJson("ratedArtSurveyResponses", []);
  return { ratingCount, viewCount, seen, responses };
}

function pickQuestionIndexByCounts(
  responses,
  excludeQuestion = null,
  seen = [],
  questions = DEFAULT_SURVEY_QUESTIONS
) {
  if (!questions.length) return 0;
  const unseen = questions.filter((question) => !seen.includes(question));
  const sourcePool = unseen.length ? unseen : questions;
  const counts = new Map(
    questions.map((question) => [question, responses?.get(question)?.total || 0])
  );
  if (excludeQuestion && counts.has(excludeQuestion)) {
    counts.set(excludeQuestion, counts.get(excludeQuestion) + 1);
  }
  let minCount = Infinity;
  sourcePool.forEach((question) => {
    const count = counts.get(question) ?? 0;
    if (count < minCount) minCount = count;
  });
  const candidates = sourcePool.filter((question) => counts.get(question) === minCount);
  const pick = candidates[Math.floor(Math.random() * candidates.length)];
  return questions.indexOf(pick);
}

function buildSurveyCountsFromResponses(responses, questions = DEFAULT_SURVEY_QUESTIONS) {
  const counts = new Map();
  const allowed = new Set(questions);
  (responses || []).forEach((entry) => {
    if (!entry?.question) return;
    if (!allowed.has(entry.question)) return;
    const current = counts.get(entry.question) || { yes: 0, no: 0, total: 0 };
    if (entry.answer === "yes") current.yes += 1;
    if (entry.answer === "no") current.no += 1;
    current.total = current.yes + current.no;
    counts.set(entry.question, current);
  });
  return counts;
}

function buildSurveyCountsFromItems(items, questions = DEFAULT_SURVEY_QUESTIONS) {
  const counts = new Map();
  const allowed = new Set(questions);
  (items || []).forEach((item) => {
    if (!item?.question) return;
    if (!allowed.has(item.question)) return;
    counts.set(item.question, {
      yes: Number(item.yes || 0),
      no: Number(item.no || 0),
      total: Number(item.total || 0)
    });
  });
  return counts;
}

function incrementSurveyCounts(prev, question, answer) {
  const next = new Map(prev || []);
  const current = next.get(question) || { yes: 0, no: 0, total: 0 };
  if (answer === "yes") current.yes += 1;
  if (answer === "no") current.no += 1;
  current.total = current.yes + current.no;
  next.set(question, current);
  return next;
}

export default function RatedArtApp() {
  const [view, setView] = useState("details");
  const [currentIndex, setCurrentIndex] = useState(0);
  const [artworksData, setArtworksData] = useState([]);
  const [totalResults, setTotalResults] = useState(0);
  const [ratedById, setRatedById] = useState(new Set());
  const [draftRatings, setDraftRatings] = useState({});
  const [userRatingsById, setUserRatingsById] = useState({});
  const [ratingModeById, setRatingModeById] = useState(new Set());
  const [surveyQuestion, setSurveyQuestionState] = useState(null);
  const [showSurvey, setShowSurvey] = useState(false);
  const requestedRatingsRef = useRef(new Set());
  const pendingCollectionUpdatesRef = useRef(new Map());
  const [surveyResponses, setSurveyResponses] = useState(() => getSurveyState().responses);
  const [seenQuestions, setSeenQuestions] = useState(() => getSurveyState().seen);
  const [surveySectionsState, setSurveySectionsState] = useState(DEFAULT_SURVEY_SECTIONS);
  const [surveyQuestionsState, setSurveyQuestionsState] = useState(DEFAULT_SURVEY_QUESTIONS);
  const [surveyCounts, setSurveyCounts] = useState(() =>
    buildSurveyCountsFromResponses(getSurveyState().responses, DEFAULT_SURVEY_QUESTIONS)
  );
  const [globalRatedArtworks, setGlobalRatedArtworks] = useState(() => {
    const stored = loadJson(GLOBAL_RATED_KEY, null);
    const parsed = Number(stored);
    return Number.isFinite(parsed) ? parsed : 0;
  });
  const [globalSyncStatus, setGlobalSyncStatus] = useState(
    HAS_SUPABASE ? "idle" : "disabled"
  );
  const [debugRatingsEnabled, setDebugRatingsEnabled] = useState(false);
  const [debugRatingsCount, setDebugRatingsCount] = useState(null);
  const [debugRatingsError, setDebugRatingsError] = useState("");
  const [debugRatingsAt, setDebugRatingsAt] = useState(0);
  const [debugRatingsProject, setDebugRatingsProject] = useState("");
  const [debugRatingsHasKey, setDebugRatingsHasKey] = useState(null);
  const [debugRatingsKeyRole, setDebugRatingsKeyRole] = useState("");
  const [debugRatingsAnonPresent, setDebugRatingsAnonPresent] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchField, setSearchField] = useState("All Fields");
  const [collectionPage, setCollectionPage] = useState(1);
  const [collectionRefresh, setCollectionRefresh] = useState(0);
  const [collectionQuery, setCollectionQuery] = useState(COLLECTION_DEFAULT_QUERY);
  const [collectionRanking, setCollectionRanking] = useState("");
  const [collectionSort, setCollectionSort] = useState("Relevance");
  const [aboutSection, setAboutSection] = useState("what-is-art");
  const [aboutMenuOpen, setAboutMenuOpen] = useState(false);
  const [collectionItems, setCollectionItems] = useState([]);
  const [collectionTotal, setCollectionTotal] = useState(0);
  const [collectionLoading, setCollectionLoading] = useState(false);
  const randomSortRef = useRef(new Map());
  const [openAccessTotal, setOpenAccessTotal] = useState(() =>
    loadJson(OPEN_ACCESS_TOTAL_KEY, 0)
  );
  const [allowBackgroundWork, setAllowBackgroundWork] = useState(false);
  const [loadedImages, setLoadedImages] = useState(new Set());
  const [loadedLargeImages, setLoadedLargeImages] = useState(new Set());
  const [compareLeftId, setCompareLeftId] = useState(null);
  const [compareRightId, setCompareRightId] = useState(null);
  const [compareStreak, setCompareStreak] = useState({ id: null, count: 0 });
  const navThrottleRef = useRef(0);
  const collectionNavRef = useRef(0);
  const [appLoading, setAppLoading] = useState(true);
  const [appLoadError, setAppLoadError] = useState("");
  const [appLoadProgress, setAppLoadProgress] = useState(0);
  const [appLoadTotal, setAppLoadTotal] = useState(0);
  const [fromCollection, setFromCollection] = useState(false);
  const [isHydrated, setIsHydrated] = useState(false);
  const [imageQuality, setImageQuality] = useState("fast");
  const [questionIndex, setQuestionIndex] = useState(0);
  const [historyIds, setHistoryIds] = useState([]);
  const [historyIndex, setHistoryIndex] = useState(-1);
  const lastViewedId = useRef(null);
  const lastSurveyInputRef = useRef(0);
  const surveyAdvanceCountRef = useRef(0);
  const collectionCache = useRef(new Map());
  const didPickInitial = useRef(false);
  const randomPool = useRef([]);
  const randomLoading = useRef(false);
  const metSearchCache = useRef(new Map());
  const recentArtists = useRef([]);
  const recentTypes = useRef([]);
  const seenArtworkIds = useRef(new Set());
  const idPoolRef = useRef([]);
  const idPoolKeyRef = useRef("");
  const idPoolOffsetRef = useRef(0);
  const navigationRequestId = useRef(0);
  const lastPrefetchId = useRef(null);
  const historyIdsRef = useRef([]);
  const historyIndexRef = useRef(-1);
  const suppressHistoryAdd = useRef(false);
  const artworkById = useRef(new Map());
  const preloadedImages = useRef(new Set());
  const objectCache = useRef(new Map());
  const objectCacheOrder = useRef([]);
  const touchStartRef = useRef({ x: 0, y: 0 });
  const touchActiveRef = useRef(false);

  const artworksList = artworksData;
  const artwork = artworksList[currentIndex % artworksList.length];
  const emptyDistribution = useMemo(() => buildEmptyDistribution(), []);
  const activeArtwork =
    artwork ||
    {
      id: "loading",
      title: "Loading…",
      artist: "",
      date: "",
      medium: "",
      objectName: "",
      culture: "",
      description: "",
      imageSmall: "",
      imageLarge: "",
      distribution: emptyDistribution
    };
  useEffect(() => {
    setView("details");
  }, []);

  useEffect(() => {
    loadCachedObjects();
    loadSeenArtworkIds();
    if (HAS_SUPABASE) {
      void flushPendingEvents();
      const handleOnline = () => {
        void flushPendingEvents();
      };
      window.addEventListener("online", handleOnline);
      return () => window.removeEventListener("online", handleOnline);
    }
  }, []);

  useEffect(() => {
    let isActive = true;
    const fetchTotal = async () => {
      const result = await fetchSearchServer({
        q: COLLECTION_DEFAULT_QUERY,
        limit: 1,
        offset: 0,
        random: true
      });
      const total = Number(result?.total || 0);
      if (!isActive || !total) return;
      setOpenAccessTotal(total);
      saveJson(OPEN_ACCESS_TOTAL_KEY, total);
    };
    void fetchTotal();
    const handleFocus = () => void fetchTotal();
    window.addEventListener("focus", handleFocus);
    return () => {
      isActive = false;
      window.removeEventListener("focus", handleFocus);
    };
  }, []);

  function getMetSearchKey(params) {
    return JSON.stringify({
      q: params.q || MET_SAMPLER_QUERY,
      limit: params.limit || 0,
      offset: params.offset || 0,
      random: Boolean(params.random),
      departmentId: params.departmentId || "",
      geoLocation: params.geoLocation || "",
      medium: params.medium || "",
      dateBegin: params.dateBegin || "",
      dateEnd: params.dateEnd || ""
    });
  }

  async function getMetSearchIds(params) {
    const isRandom = Boolean(params.random);
    const key = getMetSearchKey(params);
    if (!isRandom) {
      const cached = metSearchCache.current.get(key);
      if (cached) return cached;
    }
    const result = await fetchMetSearchHybrid(params);
    if (!isRandom && Array.isArray(result.objectIDs) && result.objectIDs.length) {
      metSearchCache.current.set(key, result);
    }
    return result;
  }

  function getIdPool(ids, excludeSeen) {
    if (!excludeSeen) return ids;
    const unseen = ids.filter((id) => !seenArtworkIds.current.has(id));
    return unseen.length ? unseen : ids;
  }

  function selectIdsFromSearch(ids, { limit, offset, random, excludeSeen }) {
    if (!ids.length) return [];
    const pool = getIdPool(ids, excludeSeen);
    if (USE_SERVER_ROUTES) return pool;
    if (random) {
      const target = Math.min(limit, pool.length);
      const picked = new Set();
      while (picked.size < target) {
        picked.add(pool[Math.floor(Math.random() * pool.length)]);
      }
      return Array.from(picked);
    }
    const start = Math.max(0, offset || 0);
    return pool.slice(start, start + limit);
  }

  useEffect(() => {
    if (!artworksList.length) return;
    if (didPickInitial.current) return;
    didPickInitial.current = true;
    (async () => {
      const index = await pickRandomArtworkIndex();
      if (index !== null) setCurrentIndex(index);
    })();
  }, [artworksList]);

  async function refreshGlobalStats(options = {}) {
    if (!HAS_SUPABASE) return;
    const { refreshSurvey = true, refreshRatings = true } = options;
    const stamp = Date.now();
    try {
      if (refreshRatings) {
        setGlobalSyncStatus((prev) => (prev === "syncing" ? prev : "syncing"));
      }
      const requests = [];
      if (refreshSurvey) {
        requests.push(
          fetch(resolveApiUrl("/api/survey", { ts: stamp }), { cache: "no-store" })
        );
      }
      if (refreshRatings) {
        requests.push(
          fetch(resolveApiUrl("/api/ratings", { summary: "global", ts: stamp }), {
            cache: "no-store"
          })
        );
      }
      const responses = await Promise.all(requests);
      let cursor = 0;
      if (refreshSurvey) {
        const surveyRes = responses[cursor];
        cursor += 1;
        if (surveyRes?.ok) {
          const surveyData = await surveyRes.json();
          const items = Array.isArray(surveyData.items) ? surveyData.items : [];
          if (items.length) {
            startTransition(() => {
              setSurveyCounts(buildSurveyCountsFromItems(items));
            });
          }
        }
      }
      if (refreshRatings) {
        const ratingRes = responses[cursor];
        if (ratingRes?.ok) {
          const ratingData = await ratingRes.json();
          const parsed = Number(ratingData.totalRatedArtworks);
          if (Number.isFinite(parsed)) {
            setGlobalRatedArtworks((prev) => {
              if (prev === parsed) return prev;
              saveJson(GLOBAL_RATED_KEY, parsed);
              return parsed;
            });
            setGlobalSyncStatus((prev) => (prev === "synced" ? prev : "synced"));
          } else {
            setGlobalSyncStatus((prev) => (prev === "stale" ? prev : "stale"));
          }
        } else {
          setGlobalSyncStatus((prev) => (prev === "stale" ? prev : "stale"));
        }
      }
    } catch {
      // ignore network failures
      setGlobalSyncStatus((prev) => (prev === "stale" ? prev : "stale"));
    }
  }

  useEffect(() => {
    if (!HAS_SUPABASE) return;
    let isActive = true;
    runWhenIdle(() => {
      if (!isActive) return;
      void refreshGlobalStats();
    });
    const interval = allowBackgroundWork
      ? setInterval(() => {
          if (!isActive) return;
          runWhenIdle(() => {
            if (!isActive) return;
            void refreshGlobalStats();
          });
        }, 15000)
      : null;
    const handleVisibility = () => {
      if (document.visibilityState === "visible") {
        runWhenIdle(() => {
          if (!isActive) return;
          void refreshGlobalStats();
        });
      }
    };
    document.addEventListener("visibilitychange", handleVisibility);
    return () => {
      isActive = false;
      if (interval) clearInterval(interval);
      document.removeEventListener("visibilitychange", handleVisibility);
    };
  }, [allowBackgroundWork]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const handleStorage = (event) => {
      if (event.key !== GLOBAL_RATED_KEY) return;
      const parsed = Number(event.newValue);
      if (Number.isFinite(parsed)) {
        setGlobalRatedArtworks(parsed);
        setGlobalSyncStatus("synced");
      }
    };
    window.addEventListener("storage", handleStorage);
    return () => window.removeEventListener("storage", handleStorage);
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const params = new URLSearchParams(window.location.search);
    const enabled =
      params.get("debug") === "ratings" ||
      localStorage.getItem("debugRatings") === "true";
    setDebugRatingsEnabled(enabled);
  }, []);

  useEffect(() => {
    if (!debugRatingsEnabled) return;
    const anonKey = process.env.NEXT_PUBLIC_SUPABASE_ANON_KEY;
    setDebugRatingsAnonPresent(Boolean(anonKey));
  }, [debugRatingsEnabled]);

  // (virtualized layout effects removed to restore scroll behavior)

  useEffect(() => {
    if (!debugRatingsEnabled) return;
    let isActive = true;
    const run = async () => {
      try {
        setDebugRatingsError("");
        const response = await fetch(
          resolveApiUrl("/api/ratings", { summary: "all", debug: "1", ts: Date.now() }),
          { cache: "no-store" }
        );
        if (!response.ok) {
          if (!isActive) return;
          setDebugRatingsError(`HTTP ${response.status}`);
          setDebugRatingsCount(null);
          setDebugRatingsAt(Date.now());
          return;
        }
        const data = await response.json();
        if (!isActive) return;
        setDebugRatingsCount(Array.isArray(data.items) ? data.items.length : 0);
        setDebugRatingsProject(data.debug?.projectRef || "");
        if (typeof data.debug?.hasServiceRoleKey === "boolean") {
          setDebugRatingsHasKey(data.debug.hasServiceRoleKey);
        }
        setDebugRatingsKeyRole(data.debug?.serviceRoleKeyRole || "");
        setDebugRatingsAt(Date.now());
      } catch {
        if (!isActive) return;
        setDebugRatingsError("Network error");
        setDebugRatingsCount(null);
        setDebugRatingsAt(Date.now());
      }
    };
    runWhenIdle(() => {
      if (!isActive) return;
      void run();
    });
    const interval = setInterval(() => {
      runWhenIdle(() => {
        if (!isActive) return;
        void run();
      });
    }, 30000);
    return () => {
      isActive = false;
      clearInterval(interval);
    };
  }, [debugRatingsEnabled]);

  useEffect(() => {
    if (!allowBackgroundWork) return;
    const baseTarget = Math.min(READY_POOL_TARGET, 48);
    const target = appLoading ? Math.min(baseTarget, 6) : baseTarget;
    void enqueueRandomArtworks(target);
  }, [totalResults, collectionTotal, allowBackgroundWork, appLoading]);

  useEffect(() => {
    if (typeof window === "undefined" || !allowBackgroundWork) return;
    const interval = setInterval(() => {
      runWhenIdle(() => {
        if (randomLoading.current) return;
        const query = buildCollectionQueryTerm(collectionQuery);
        const poolParams = { q: query };
        void ensureIdPool(poolParams);
        if (randomPool.current.length < READY_POOL_THRESHOLD) {
          void enqueueRandomArtworks(READY_POOL_TARGET);
        }
      });
    }, 20000);
    return () => clearInterval(interval);
  }, [collectionQuery, allowBackgroundWork]);
  const totalAccessible =
    openAccessTotal ||
    (collectionQuery === COLLECTION_DEFAULT_QUERY ? collectionTotal || 0 : 0);
  const ratedCount = ratedById.size;
  const hasGlobalCount = HAS_SUPABASE && Number.isFinite(globalRatedArtworks);
  const displayedRatingCount =
    hasGlobalCount && globalSyncStatus === "synced" ? globalRatedArtworks : 0;
  const syncLabel = (() => {
    if (!HAS_SUPABASE) return "LOCAL";
    if (globalSyncStatus === "syncing") return "SYNCING…";
    if (globalSyncStatus === "stale") return "SYNCING…";
    if (globalSyncStatus === "synced") return "GLOBAL";
    return "LOCAL";
  })();
  const progressLabel = `ARTWORKS RATED · ${syncLabel}`;
  const safeDisplayedRatingCount = isHydrated ? displayedRatingCount : 0;
  const safeTotalAccessible = isHydrated ? totalAccessible : 0;
  const progressPercent = safeTotalAccessible
    ? Math.min(100, ((safeDisplayedRatingCount || 0) / safeTotalAccessible) * 100)
    : 0;

  const artistCounts = useMemo(() => {
    const counts = new Map();
    artworksList.forEach((art) => {
      const artist = (art.artist || "").trim();
      if (!artist || artist.toLowerCase() === "unknown") return;
      counts.set(artist, (counts.get(artist) || 0) + 1);
    });
    return counts;
  }, [artworksList]);

  const globalStats = useMemo(() => {
    const stats = {};
    criteria.forEach((criterion) => {
      const aggregated = artworksList.reduce((acc, art) => {
        const dist = art.distribution?.[criterion] || [0, 0, 0, 0, 0];
        return dist.map((count, index) => count + acc[index]);
      }, [0, 0, 0, 0, 0]);
      const { total, mean, variance } = computeDistributionStats(aggregated);
      stats[criterion] = {
        total,
        mean: total ? mean : 3,
        variance: total ? variance : 1
      };
    });
    return stats;
  }, [artworksList]);

  function getBayesianScores(art) {
    const k = 12;
    let quality = 0;
    let varianceSum = 0;

    criteria.forEach((criterion) => {
      const weight = CRITERIA_WEIGHTS[criterion] || 0;
      const dist = art.distribution?.[criterion] || [0, 0, 0, 0, 0];
      const { total, mean, variance } = computeDistributionStats(dist);
      const global = globalStats[criterion];
      const priorMean = global?.mean ?? 3;
      const priorVar = global?.variance ?? 1;
      const n = total;
      const postMean = (k * priorMean + n * mean) / (k + n);
      const se = Math.sqrt((n ? variance : priorVar) / Math.max(n, 1));
      quality += weight * postMean;
      varianceSum += Math.pow(weight * se, 2);
    });

    const seQ = Math.sqrt(varianceSum);
    const lcb = quality - 1.64 * seQ;
    return { quality, lcb };
  }

  function formatBayesScore(art) {
    if (!art) return "–";
    const average = computeOverallAverage(art);
    if (!average) return "–";
    return average.toFixed(1);
  }

  function formatBayesLcb(art) {
    if (!art) return "–";
    if (art.displayScore) return art.displayScore.toFixed(1);
    const { lcb } = getBayesianScores(art);
    if (!lcb) return "–";
    return lcb.toFixed(1);
  }

  function getConfidenceLabel(art) {
    const dist = art?.distribution?.["Overall Impression"] || [0, 0, 0, 0, 0];
    const total = sum(dist);
    if (total >= 50) return "High confidence";
    if (total >= 15) return "Medium confidence";
    return "Low confidence";
  }

  function hasCommunityRatings(art) {
  const distribution = getRatingDistribution(art);
  if (!distribution) return false;
  return criteria.some((criterion) => sum(distribution[criterion] || []) > 0);
  }

  function getArtworkDescription(art) {
    if (!art) return "";
    if (art.description) return art.description;
    const parts = [
      art.objectName,
      art.medium,
      art.culture,
      art.period,
      art.classification,
      art.creditLine,
      art.artistDisplayBio
    ].filter(Boolean);
    return parts.join(" · ");
  }

  function openArtistCollection(artist) {
    if (!artist) return;
    setSearchQuery(artist);
    setCollectionQuery(artist);
    setCollectionPage(1);
    setView("collection");
    setFromCollection(false);
  }

  function registerArtworks(items) {
    const resolvedItems = [];
    items.forEach((item) => {
      if (!item || !item.id) return;
      const existing = artworkById.current.get(item.id);
      const resolved = existing?.__userRated ? existing : item;
      artworkById.current.set(item.id, resolved);
      setCachedObject(item.id, resolved);
      resolvedItems.push(resolved);
    });
    setCachedObjects(resolvedItems);
  }

  function updateArtworkDistribution(art, scores, previousScores) {
    if (!art) return art;
    const baseSource = art.__baseDistribution || art.distribution;
    const nextDistribution = cloneDistribution(baseSource);
    criteria.forEach((criterion) => {
      const current = [...(nextDistribution[criterion] || [0, 0, 0, 0, 0])];
      if (previousScores?.[criterion]) {
        const prevIndex = previousScores[criterion] - 1;
        current[prevIndex] = Math.max(0, current[prevIndex] - 1);
      }
      const nextIndex = scores[criterion] - 1;
      current[nextIndex] += 1;
      nextDistribution[criterion] = current;
    });
    const distribution = art.__summaryDistribution
      ? mergeDistribution(nextDistribution, art.__summaryDistribution)
      : nextDistribution;
    const updated = {
      ...art,
      distribution,
      __baseDistribution: nextDistribution,
      __userRated: true
    };
    artworkById.current.set(art.id, updated);
    return updated;
  }

  function updateCollectionCacheItem(updatedArt) {
    collectionCache.current.forEach((state) => {
      if (!state?.pages) return;
      state.pages.forEach((pageItems, pageNumber) => {
        if (!Array.isArray(pageItems)) return;
        let changed = false;
        const nextItems = pageItems.map((item) => {
          if (item?.id !== updatedArt.id) return item;
          changed = true;
          return updatedArt;
        });
        if (changed) {
          state.pages.set(pageNumber, nextItems);
        }
      });
    });
  }

  function mergeDistribution(base, incoming) {
    const next = { ...base };
    Object.entries(incoming || {}).forEach(([criterion, values]) => {
      const current = [...(next[criterion] || [0, 0, 0, 0, 0])];
      values.forEach((count, index) => {
        current[index] += count;
      });
      next[criterion] = current;
    });
    return next;
  }

  function applyStoredRatingsToItems(items) {
    if (!items.length) return items;
    let changed = false;
    const nextItems = items.map((item) => {
      const scores = userRatingsById[item.id];
      if (!scores) return item;
      const cached = artworkById.current.get(item.id);
      if (cached?.__userRated) return cached;
      const updated = updateArtworkDistribution(cached || item, scores);
      updateCollectionCacheItem(updated);
      changed = true;
      return updated;
    });
    return changed ? nextItems : items;
  }

  async function fetchRatingsSummary(ids) {
    if (!ids.length) return new Map();
    try {
      const response = await fetch(
        resolveApiUrl("/api/ratings", { ids: ids.join(",") }),
        { cache: "no-store" }
      );
      if (!response.ok) return new Map();
      const data = await response.json();
      const summary = new Map();
      (data.items || []).forEach((item) => {
        summary.set(String(item.artworkId), item.distribution || {});
      });
      return summary;
    } catch {
      return new Map();
    }
  }

  function applyRatingsSummaryToItems(items, summary) {
    if (!items.length || !summary.size) return items;
    let changed = false;
    const nextItems = items.map((item) => {
      const incoming = summary.get(String(item.id));
      if (!incoming) return item;
      const incomingKey = JSON.stringify(incoming);
      if (item.__summaryKey === incomingKey && item.__summaryDistribution) return item;
      let base;
      if (item.__baseDistribution) {
        base = cloneDistribution(item.__baseDistribution);
      } else if (item.__summaryDistribution) {
        base = subtractDistribution(item.distribution, item.__summaryDistribution);
      } else {
        base = cloneDistribution(item.distribution);
      }
      const updated = {
        ...item,
        __baseDistribution: base,
        __summaryDistribution: incoming,
        __summaryKey: incomingKey,
        distribution: mergeDistribution(base, incoming)
      };
      artworkById.current.set(item.id, updated);
      updateCollectionCacheItem(updated);
      changed = true;
      return updated;
    });
    return changed ? nextItems : items;
  }

  function getArtworkById(id) {
    if (!id) return null;
    return (
      artworkById.current.get(id) ||
      artworksList.find((art) => art.id === id) ||
      null
    );
  }

  function LazyImage({ src, srcSet, sizes, alt, onLoad, fetchPriority = "low" }) {
    return (
      <img
        src={src}
        srcSet={srcSet}
        sizes={sizes}
        alt={alt}
        loading="lazy"
        decoding="async"
        fetchPriority={fetchPriority}
        onLoad={onLoad}
      />
    );
  }

  function loadCachedObjects() {
    if (typeof window === "undefined") return;
    try {
      const currentVersion = localStorage.getItem("ratedArtObjectCacheVersion");
      if (currentVersion !== OBJECT_CACHE_VERSION) {
        localStorage.removeItem("ratedArtObjectCache");
        localStorage.setItem("ratedArtObjectCacheVersion", OBJECT_CACHE_VERSION);
        objectCache.current = new Map();
        objectCacheOrder.current = [];
        try {
          indexedDB.deleteDatabase("rated-art-cache");
        } catch {
          // ignore
        }
        return;
      }
      const raw = localStorage.getItem("ratedArtObjectCache");
      if (!raw) return;
      const data = JSON.parse(raw);
      if (!data?.items || !Array.isArray(data.order)) return;
      objectCache.current = new Map(Object.entries(data.items));
      objectCacheOrder.current = data.order;
    } catch {
      // ignore
    }
  }

  function loadSeenArtworkIds() {
    const seen = loadJson(SEEN_ARTWORKS_KEY, []);
    if (Array.isArray(seen) && seen.length) {
      seenArtworkIds.current = new Set(seen);
    }
  }

  function getIdPoolCacheKey(params) {
    return JSON.stringify({
    v: ID_POOL_CACHE_VERSION,
    catalog: USE_SUPABASE_CATALOG,
      q: params.q || MET_SAMPLER_QUERY
    });
  }

  function loadIdPoolCache(cacheKey) {
    const cached = loadJson(ID_POOL_CACHE_KEY, null);
    if (!cached || cached.key !== cacheKey || !Array.isArray(cached.ids)) return;
    idPoolRef.current = cached.ids.map(normalizeId).filter(Boolean);
    idPoolKeyRef.current = cacheKey;
    idPoolOffsetRef.current = Number(cached.nextOffset || 0);
  }

  function saveIdPoolCache(cacheKey) {
    saveJson(ID_POOL_CACHE_KEY, {
      key: cacheKey,
      ids: idPoolRef.current.slice(0, ID_POOL_MAX),
      nextOffset: idPoolOffsetRef.current,
      updatedAt: new Date().toISOString()
    });
  }

  function resetIdPool(cacheKey) {
    idPoolKeyRef.current = cacheKey;
    idPoolRef.current = [];
    idPoolOffsetRef.current = 0;
    saveIdPoolCache(cacheKey);
  }

  async function ensureIdPool(params, target = ID_POOL_TARGET) {
    const cacheKey = getIdPoolCacheKey(params);
    if (idPoolKeyRef.current !== cacheKey) {
      loadIdPoolCache(cacheKey);
      if (idPoolKeyRef.current !== cacheKey) {
        resetIdPool(cacheKey);
      }
    }
    let attempts = 0;
    let didResetOffset = false;
    while (idPoolRef.current.length < target && attempts < 5) {
      const data = await getMetSearchIds({
        q: params.q,
        limit: ID_POOL_BATCH,
        offset: idPoolOffsetRef.current,
        random: false
      });
      const totalResults = Number(data.total || 0);
      if (totalResults && idPoolOffsetRef.current >= totalResults && !didResetOffset) {
        idPoolOffsetRef.current = 0;
        metSearchCache.current.clear();
        didResetOffset = true;
        continue;
      }
      const rawIds = Array.isArray(data.objectIDs) ? data.objectIDs : [];
      idPoolOffsetRef.current += rawIds.length;
      const picked = dedupeIds(
        selectIdsFromSearch(rawIds.map(normalizeId).filter(Boolean), {
          limit: ID_POOL_BATCH,
          offset: 0,
          random: false,
          excludeSeen: false
        })
      );
      if (!picked.length) {
        attempts += 1;
        continue;
      }
      const merged = new Set(idPoolRef.current);
      picked.forEach((id) => merged.add(id));
      idPoolRef.current = Array.from(merged).slice(0, ID_POOL_MAX);
      saveIdPoolCache(cacheKey);
      attempts += 1;
    }
  }

  async function pickIdsFromPool(params, count, excludeSet) {
    const minTarget = Math.max(ID_POOL_TARGET, count * 6);
    await ensureIdPool(params, minTarget);
    let candidates = idPoolRef.current.filter((id) => !excludeSet.has(id));
    if (candidates.length < count && idPoolRef.current.length < ID_POOL_MAX) {
      let target = Math.min(
        ID_POOL_MAX,
        Math.max(minTarget, idPoolRef.current.length + ID_POOL_BATCH * 3)
      );
      let attempts = 0;
      while (candidates.length < count && attempts < 3) {
        await ensureIdPool(params, target);
        candidates = idPoolRef.current.filter((id) => !excludeSet.has(id));
        if (idPoolRef.current.length >= ID_POOL_MAX) break;
        target = Math.min(ID_POOL_MAX, target + ID_POOL_BATCH * 2);
        attempts += 1;
      }
    }
    if (!candidates.length) {
      // Pool exhausted; allow repeats if we have nothing new.
      candidates = idPoolRef.current.slice();
    }
    if (!candidates.length) return [];
    const target = Math.min(count, candidates.length);
    const picked = new Set();
    while (picked.size < target) {
      picked.add(candidates[Math.floor(Math.random() * candidates.length)]);
    }
    return Array.from(picked);
  }

  function persistSeenArtworkIds() {
    saveJson(
      SEEN_ARTWORKS_KEY,
      Array.from(seenArtworkIds.current).slice(-SEEN_ARTWORKS_LIMIT)
    );
  }

  function resetSeenArtworkIds() {
    seenArtworkIds.current = new Set();
    saveJson(SEEN_ARTWORKS_KEY, []);
  }

  function persistCachedObjects() {
    if (typeof window === "undefined") return;
    try {
      const items = Object.fromEntries(objectCache.current.entries());
      localStorage.setItem(
        "ratedArtObjectCache",
        JSON.stringify({ items, order: objectCacheOrder.current })
      );
    } catch {
      // ignore
    }
  }

  function setCachedObject(id, value) {
    if (!id || !value) return;
    objectCache.current.set(id, value);
    objectCacheOrder.current = [
      id,
      ...objectCacheOrder.current.filter((key) => key !== id)
    ].slice(0, 200);
    persistCachedObjects();
  }

  function getCachedObject(id) {
    return objectCache.current.get(id) || null;
  }

  function openObjectDb() {
    if (typeof window === "undefined") return null;
    return new Promise((resolve) => {
      const request = indexedDB.open("rated-art-cache", 1);
      request.onupgradeneeded = () => {
        const db = request.result;
        if (!db.objectStoreNames.contains("objects")) {
          db.createObjectStore("objects");
        }
      };
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => resolve(null);
    });
  }

  async function getCachedObjects(ids) {
    const db = await openObjectDb();
    if (!db) return new Map();
    return new Promise((resolve) => {
      const tx = db.transaction("objects", "readonly");
      const store = tx.objectStore("objects");
      const results = new Map();
      let remaining = ids.length;
      if (!remaining) resolve(results);
      ids.forEach((id) => {
        const req = store.get(id);
        req.onsuccess = () => {
          if (req.result) results.set(id, req.result);
          remaining -= 1;
          if (!remaining) resolve(results);
        };
        req.onerror = () => {
          remaining -= 1;
          if (!remaining) resolve(results);
        };
      });
    });
  }

  async function setCachedObjects(items) {
    const db = await openObjectDb();
    if (!db || !items.length) return;
    const tx = db.transaction("objects", "readwrite");
    const store = tx.objectStore("objects");
    items.forEach((item) => {
      store.put(item, String(item.id));
    });
  }

  function getPriorityPools() {
    const poolUnrated = [];
    const poolUnratedByUser = [];
    const poolRatedByUser = [];

    artworksList.forEach((art) => {
      if (seenArtworkIds.current.has(art.id)) return;
      const communityRated = hasCommunityRatings(art);
      if (!communityRated) {
        poolUnrated.push(art);
      } else if (!ratedById.has(art.id)) {
        poolUnratedByUser.push(art);
      } else {
        poolRatedByUser.push(art);
      }
    });

    return [poolUnrated, poolUnratedByUser, poolRatedByUser];
  }

  function canRepeatArtwork() {
    const total = totalResults || collectionTotal;
    if (!total || total <= 0) return true;
    return seenArtworkIds.current.size >= total;
  }

  function filterUnseen(candidates, excludeSet, options = {}) {
    const { ignoreRecentArtists = false, ignoreRecentTypes = false } = options;
    const allowRepeat = canRepeatArtwork();
    return candidates.filter((art) => {
      if (excludeSet.has(art.id)) return false;
      if (!allowRepeat && seenArtworkIds.current.has(art.id)) return false;
      if (!ignoreRecentArtists && recentArtists.current.includes(art.artist)) return false;
      const typeKey = art.objectName || art.medium;
      if (!ignoreRecentTypes && typeKey && recentTypes.current.includes(typeKey)) return false;
      return true;
    });
  }

  async function waitForRandomLoad() {
    if (!randomLoading.current) return;
    for (let i = 0; i < 10; i += 1) {
      await new Promise((resolve) => setTimeout(resolve, 120));
      if (!randomLoading.current) return;
    }
  }

  function ensureRandomPool(minSize = READY_POOL_THRESHOLD, batchSize = READY_POOL_TARGET) {
    if (randomLoading.current) return;
    if (randomPool.current.length >= minSize) return;
    void enqueueRandomArtworks(batchSize);
  }

  async function getNextArtworkId(excludeIds = []) {
    if (!artworksList.length && !randomPool.current.length) return null;
    const excludeSet = new Set(excludeIds);

    if (randomPool.current.length < READY_POOL_THRESHOLD && !randomLoading.current) {
      void enqueueRandomArtworks(READY_POOL_TARGET);
    }

    const poolCandidates = filterUnseen(randomPool.current, excludeSet);
    if (poolCandidates.length) {
      const pick = poolCandidates[Math.floor(Math.random() * poolCandidates.length)];
      randomPool.current = randomPool.current.filter((art) => art.id !== pick.id);
      return pick.id;
    }

    const pools = getPriorityPools();
    for (const pool of pools) {
      const candidates = filterUnseen(pool, excludeSet);
      if (candidates.length) {
        const pick = candidates[Math.floor(Math.random() * candidates.length)];
        return pick.id;
      }
    }

    if (!canRepeatArtwork()) {
      await enqueueRandomArtworks(READY_POOL_TARGET);
      const expandedPool = filterUnseen(randomPool.current, excludeSet);
      if (expandedPool.length) {
        const pick = expandedPool[Math.floor(Math.random() * expandedPool.length)];
        randomPool.current = randomPool.current.filter((art) => art.id !== pick.id);
        return pick.id;
      }

      const relaxedPool = filterUnseen(randomPool.current, excludeSet, {
        ignoreRecentArtists: true
      });
      if (relaxedPool.length) {
        const pick = relaxedPool[Math.floor(Math.random() * relaxedPool.length)];
        randomPool.current = randomPool.current.filter((art) => art.id !== pick.id);
        return pick.id;
      }

      for (const pool of pools) {
        const candidates = filterUnseen(pool, excludeSet, { ignoreRecentArtists: true });
        if (candidates.length) {
          const pick = candidates[Math.floor(Math.random() * candidates.length)];
          return pick.id;
        }
      }

      await waitForRandomLoad();
      const retryCandidates = filterUnseen(randomPool.current, excludeSet, {
        ignoreRecentArtists: true
      });
      if (retryCandidates.length) {
        const pick = retryCandidates[Math.floor(Math.random() * retryCandidates.length)];
        randomPool.current = randomPool.current.filter((art) => art.id !== pick.id);
        return pick.id;
      }
      return null;
    }

    const fallbackCandidates = artworksList.filter((art) => !excludeSet.has(art.id));
    if (fallbackCandidates.length) {
      const pick = fallbackCandidates[Math.floor(Math.random() * fallbackCandidates.length)];
      return pick.id;
    }
    return null;
  }

  async function pickRandomArtworkIndex(excludeIds = []) {
    const id = await getNextArtworkId(excludeIds);
    if (!id) return null;
    const index = artworksList.findIndex((art) => art.id === id);
    if (index !== -1) return index;
    const fromPool = randomPool.current.find((art) => art.id === id);
    if (fromPool) {
      registerArtworks([fromPool]);
      setArtworksData((prev) => [fromPool, ...prev.filter((art) => art.id !== id)]);
      return 0;
    }
    return null;
  }

  async function loadFreshRandomBatch(batchSize = 36) {
    try {
      const poolParams = { q: MET_SAMPLER_QUERY };
      const ids = await pickIdsFromPool(poolParams, batchSize, new Set());
      if (!ids.length) return;
      let fetched = await fetchMetObjectsWithRetries(ids, {
        concurrency: 6,
        batchSize: DETAILS_BATCH_SIZE
      });
      if (!fetched.length) return;

      const normalized = await normalizeArtworks(fetched);

      const hydrated = applyStoredRatingsToItems(normalized);
      registerArtworks(hydrated);
      const summary = await fetchRatingsSummary(hydrated.map((item) => item.id));
      await new Promise((resolve) => setTimeout(resolve, 0));
      const merged = applyRatingsSummaryToItems(hydrated, summary);
      randomPool.current = [];
      resetSeenArtworkIds();
      const shuffled = merged.sort(() => Math.random() - 0.5);
      startTransition(() => {
        setArtworksData(shuffled);
      });
      setCurrentIndex(0);
      historyIdsRef.current = [];
      historyIndexRef.current = -1;
      setHistoryIds([]);
      setHistoryIndex(-1);
    } catch (error) {
      console.error(error);
    }
  }

  useEffect(() => {
    if (!activeArtwork || !activeArtwork.artist) return;
    const next = [...recentArtists.current.filter((name) => name !== activeArtwork.artist)];
    next.unshift(activeArtwork.artist);
    recentArtists.current = next.slice(0, 8);
  }, [activeArtwork.id]);

  useEffect(() => {
    const typeKey = activeArtwork.objectName || activeArtwork.medium;
    if (!typeKey) return;
    const next = [...recentTypes.current.filter((name) => name !== typeKey)];
    next.unshift(typeKey);
    recentTypes.current = next.slice(0, 30);
  }, [activeArtwork.id]);

  useEffect(() => {
    if (!activeArtwork || !activeArtwork.id || activeArtwork.id === "loading") return;
    markSeenArtwork(activeArtwork.id);
  }, [activeArtwork.id]);

  useEffect(() => {
    historyIdsRef.current = historyIds;
  }, [historyIds]);

  useEffect(() => {
    historyIndexRef.current = historyIndex;
  }, [historyIndex]);

  useEffect(() => {
    if (view !== "details") return;
    const id = activeArtwork?.id;
    if (!id) return;
    if (suppressHistoryAdd.current) {
      suppressHistoryAdd.current = false;
      return;
    }
    const ids = historyIdsRef.current;
    const idx = historyIndexRef.current;
    if (idx >= 0 && ids[idx] === id) return;
    let nextIds = ids;
    if (idx >= 0 && idx < ids.length - 1) {
      nextIds = ids.slice(0, idx + 1);
    }
    if (!nextIds.length || nextIds[nextIds.length - 1] !== id) {
      nextIds = [...nextIds, id];
    }
    const nextIndex = nextIds.length - 1;
    historyIdsRef.current = nextIds;
    historyIndexRef.current = nextIndex;
    setHistoryIds(nextIds);
    setHistoryIndex(nextIndex);
  }, [view, activeArtwork?.id]);

  useEffect(() => {
    if (view !== "details") return;
    if (!allowBackgroundWork) return;
    runWhenIdle(() => {
      ensureRandomPool(6, 12);
    });
    const interval = setInterval(() => {
      runWhenIdle(() => {
        ensureRandomPool(6, 12);
      });
    }, 30000);
    return () => clearInterval(interval);
  }, [view, collectionQuery]);

  useEffect(() => {
    if (!showSurvey) return;
    ensureRandomPool(SURVEY_PREFETCH_TARGET, SURVEY_PREFETCH_TARGET);
  }, [showSurvey]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const storedRatings = loadJson("ratedArtUserRatings", {});
    const storedRatedIds = loadJson("ratedArtRatedIds", []);
    if (!storedRatings || !Object.keys(storedRatings).length) {
      if (storedRatedIds?.length) {
        startTransition(() => {
          setRatedById(new Set(storedRatedIds.map((id) => Number(id))));
        });
      }
      return;
    }
    const ratingIds = Object.keys(storedRatings).map((id) => Number(id));
    setUserRatingsById(storedRatings);
    startTransition(() => {
      setRatedById(new Set([...storedRatedIds, ...ratingIds].map((id) => Number(id))));
      setArtworksData((prev) => applyStoredRatingsToItems(prev));
      setCollectionItems((prev) => applyStoredRatingsToItems(prev));
    });
  }, []);

  useEffect(() => {
    if (activeArtwork?.imageSmall) {
      preloadImage(activeArtwork.imageSmall, `${activeArtwork.id}-small`, 600);
    }
    if (imageQuality === "high" && activeArtwork?.imageLarge) {
      preloadLargeImage(activeArtwork.imageLarge, activeArtwork.id, 1600);
    }
    if (!allowBackgroundWork) return;
    const prefetchCount =
      typeof window !== "undefined" && window.innerWidth <= 720 ? 2 : 4;
    randomPool.current.slice(0, prefetchCount).forEach((art) => {
      if (art.imageSmall) preloadImage(art.imageSmall, `${art.id}-small`, 400);
    });
  }, [activeArtwork?.id, allowBackgroundWork]);

  useEffect(() => {
    if (!activeArtwork?.id) return;
    if (!allowBackgroundWork) return;
    if (!loadedImages.has(activeArtwork.id)) return;
    if (lastPrefetchId.current === activeArtwork.id) return;
    lastPrefetchId.current = activeArtwork.id;
    (async () => {
      if (randomPool.current.length < 8) {
        await enqueueRandomArtworks(READY_POOL_TARGET);
      }
      const candidate = randomPool.current.find((art) => art.imageSmall || art.imageLarge);
      if (!candidate) return;
      if (candidate.imageSmall) preloadImage(candidate.imageSmall, `${candidate.id}-small`, 400);
      if (imageQuality === "high" && candidate.imageLarge) {
        preloadLargeImage(candidate.imageLarge, candidate.id, 1600);
      }
    })();
  }, [activeArtwork?.id, loadedImages, allowBackgroundWork]);

  const detailImageSrc = proxyImageUrl(
    activeArtwork.imageSmall || activeArtwork.imageLarge,
    `${activeArtwork.id}-small`,
    600
  );
  const detailImageSrcSet =
    imageQuality === "high" && activeArtwork.imageSmall && activeArtwork.imageLarge
      ? `${proxyImageUrl(activeArtwork.imageSmall, `${activeArtwork.id}-small`, 600)} 600w, ${proxyImageUrl(activeArtwork.imageLarge, `${activeArtwork.id}-large`, 1600)} 1600w`
      : undefined;

  useEffect(() => {
    if (view !== "questions") return;
    setQuestionIndex(pickQuestionIndexByCounts(surveyCounts, null, seenQuestions));
  }, [view]);

  useEffect(() => {
    if (!surveyQuestion) return;
    let nextSeen = null;
    setSeenQuestions((prev) => {
      if (prev.includes(surveyQuestion)) return prev;
      const next = [...prev, surveyQuestion];
      saveJson("ratedArtSeenQuestions", next);
      nextSeen = next;
      return next;
    });
  }, [surveyQuestion]);

  useEffect(() => {
    if (view !== "compare") return;
    if (compareLeftId) markSeenArtwork(compareLeftId);
    if (compareRightId) markSeenArtwork(compareRightId);
  }, [view, compareLeftId, compareRightId]);

  function markImageLoaded(id) {
    setLoadedImages((prev) => {
      if (prev.has(id)) return prev;
      const next = new Set(prev);
      next.add(id);
      return next;
    });
  }

  function allowNavigation(ref, throttleMs) {
    const now = Date.now();
    if (now - ref.current < throttleMs) return false;
    ref.current = now;
    return true;
  }

  function markLargeLoaded(id) {
    setLoadedLargeImages((prev) => {
      if (prev.has(id)) return prev;
      const next = new Set(prev);
      next.add(id);
      return next;
    });
  }

  function handleSwipeStart(event) {
    if (!event.touches || event.touches.length !== 1) return;
    const target = event.target;
    if (target?.closest?.("button, a, input, select, textarea, .survey-actions")) return;
    const touch = event.touches[0];
    touchStartRef.current = { x: touch.clientX, y: touch.clientY };
    touchActiveRef.current = true;
  }

  function handleSwipeEnd(event) {
    if (!touchActiveRef.current) return;
    touchActiveRef.current = false;
    const touch = event.changedTouches?.[0];
    if (!touch) return;
    const dx = touch.clientX - touchStartRef.current.x;
    const dy = touch.clientY - touchStartRef.current.y;
    if (Math.abs(dx) < 50 || Math.abs(dx) < Math.abs(dy) * 1.2) return;
    void navigateArtwork(dx < 0 ? "next" : "prev");
  }

  function handleSurveySwipeEnd(event) {
    if (!touchActiveRef.current) return;
    touchActiveRef.current = false;
    const target = event.target;
    if (target?.closest?.("button, a, input, select, textarea, .survey-actions")) return;
    const touch = event.changedTouches?.[0];
    if (!touch) return;
    const dx = touch.clientX - touchStartRef.current.x;
    const dy = touch.clientY - touchStartRef.current.y;
    if (Math.abs(dx) < 50 || Math.abs(dx) < Math.abs(dy) * 1.2) return;
    if (showSurvey) {
      advanceSurveyQuestion(surveyQuestion);
      return;
    }
    void navigateArtwork(dx < 0 ? "next" : "prev");
  }
  
  const surveyAnswerCountRef = useRef(0);

  async function transitionToAvailableArtworkFromSurvey() {
    setFromCollection(false);
    setShowSurvey(false);
    setView("details");
    let index = await pickRandomArtworkIndex();
    if (index === null && artworksList.length) {
      index = 0;
    }
    if (index !== null) {
      setCurrentIndex(index);
      return true;
    }
    runWhenIdle(() => {
      void enqueueRandomArtworks(RATING_PREFETCH_TARGET);
    });
    return false;
  }

  async function navigateArtwork(direction) {
    if (!allowNavigation(navThrottleRef, NAV_THROTTLE_MS)) return;
    if (!artworksList.length && !randomPool.current.length) return;
    if (fromCollection) {
      const list = collectionItems.length ? collectionItems : artworksList;
      if (!list.length) return;
      const activeId = activeArtwork?.id;
      const currentIdx = list.findIndex((art) => art.id === activeId);
      const baseIdx = currentIdx === -1 ? 0 : currentIdx;
      const nextIdx =
        direction === "next"
          ? (baseIdx + 1) % list.length
          : (baseIdx - 1 + list.length) % list.length;
      const nextItem = list[nextIdx];
      if (!nextItem) return;
      const targetIndex = artworksList.findIndex((art) => art.id === nextItem.id);
      if (targetIndex !== -1) {
        setCurrentIndex(targetIndex);
      } else {
        setArtworksData((prev) => [
          nextItem,
          ...prev.filter((art) => art.id !== nextItem.id)
        ]);
        setCurrentIndex(0);
      }
      return;
    }

    const ids = historyIdsRef.current;
    const idx = historyIndexRef.current;
    if (direction === "prev" && idx > 0) {
      const targetId = ids[idx - 1];
      suppressHistoryAdd.current = true;
      historyIndexRef.current = idx - 1;
      setHistoryIndex(idx - 1);
      const targetIndex = artworksList.findIndex((art) => art.id === targetId);
      if (targetIndex !== -1) {
        setCurrentIndex(targetIndex);
      } else {
        const fallback = artworkById.current.get(targetId);
        if (fallback) {
          setArtworksData((prev) => [fallback, ...prev.filter((art) => art.id !== targetId)]);
          setCurrentIndex(0);
        }
      }
      return;
    }
    if (direction === "next" && idx >= 0 && idx < ids.length - 1) {
      const targetId = ids[idx + 1];
      suppressHistoryAdd.current = true;
      historyIndexRef.current = idx + 1;
      setHistoryIndex(idx + 1);
      const targetIndex = artworksList.findIndex((art) => art.id === targetId);
      if (targetIndex !== -1) {
        setCurrentIndex(targetIndex);
      } else {
        const fallback = artworkById.current.get(targetId);
        if (fallback) {
          setArtworksData((prev) => [fallback, ...prev.filter((art) => art.id !== targetId)]);
          setCurrentIndex(0);
        }
      }
      return;
    }

    const requestId = navigationRequestId.current + 1;
    navigationRequestId.current = requestId;

    let immediateIndex = null;
    if (artworksList.length) {
      immediateIndex =
        direction === "next"
          ? (currentIndex + 1) % artworksList.length
          : (currentIndex - 1 + artworksList.length) % artworksList.length;
      setCurrentIndex(immediateIndex);
    }

    const excludeIds = activeArtwork?.id ? [activeArtwork.id] : [];
    if (immediateIndex !== null) {
      const immediateArtwork = artworksList[immediateIndex];
      if (immediateArtwork?.id) excludeIds.push(immediateArtwork.id);
    }
    let index = await pickRandomArtworkIndex(excludeIds);
    if (index === null) {
      await enqueueRandomArtworks(READY_POOL_TARGET);
      index = await pickRandomArtworkIndex(excludeIds);
    }
    if (index !== null && navigationRequestId.current === requestId) {
      setCurrentIndex(index);
    }
  }

  function proxyImageUrl(url, cacheKey, width) {
    if (!url) return "";
    if (!IMAGE_PROXY_ENABLED) {
      if (IMAGE_CDN_ENABLED && !url.startsWith("/.netlify/images")) {
        return resolveApiUrl("/.netlify/images", {
          url,
          w: width || 800,
          q: IMAGE_CDN_QUALITY,
          fit: "contain"
        });
      }
      if (url.startsWith("/api/image")) {
        try {
          const parsed = new URL(
            url,
            typeof window !== "undefined" ? window.location.origin : "http://localhost"
          );
          return parsed.searchParams.get("url") || url;
        } catch {
          return url;
        }
      }
      return url;
    }
    if (url.startsWith("/api/image")) return url;
    if (IMAGE_CDN_ENABLED && !url.startsWith("/.netlify/images")) {
      return resolveApiUrl("/.netlify/images", {
        url,
        w: width || 800,
        q: IMAGE_CDN_QUALITY,
        fit: "contain"
      });
    }
    const keyParam = cacheKey ? `&v=${encodeURIComponent(cacheKey)}` : "";
    return `/api/image?url=${encodeURIComponent(url)}${keyParam}`;
  }

  function preloadImage(url, cacheKey, width) {
    const targetUrl = proxyImageUrl(url, cacheKey, width);
    if (!targetUrl || preloadedImages.current.has(targetUrl)) return;
    const img = new Image();
    img.decoding = "async";
    img.src = targetUrl;
    preloadedImages.current.add(targetUrl);
  }

  function preloadLargeImage(url, id, width) {
    const targetUrl = proxyImageUrl(url, id ? `${id}-large` : undefined, width);
    if (!targetUrl || !id) return;
    const img = new Image();
    img.decoding = "async";
    img.onload = () => markLargeLoaded(id);
    img.src = targetUrl;
  }

  const draftScores = draftRatings[activeArtwork.id] || {};
  const userScores = userRatingsById[activeArtwork.id] || draftScores;
  const isComplete = criteria.every((key) => draftScores[key]);
  const compositeDistribution = useMemo(() => {
    return criteria.reduce(
      (acc, criterion) => {
        const dist = activeArtwork.distribution?.[criterion] || [0, 0, 0, 0, 0];
        return acc.map((value, index) => value + dist[index]);
      },
      [0, 0, 0, 0, 0]
    );
  }, [activeArtwork.id, activeArtwork.distribution]);
  const totalReviews = Math.round(sum(compositeDistribution) / criteria.length);

  const [ratingHeaderText, setRatingHeaderText] = useState(
    "Not an expert? Go ahead, rate this artwork."
  );

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  useEffect(() => {
    setAllowBackgroundWork(view !== "details");
  }, [view]);

  useEffect(() => {
    runWhenIdle(() => {
      if (typeof document !== "undefined") {
        document.body.classList.add("bg-animate");
      }
    });
    return () => {
      if (typeof document !== "undefined") {
        document.body.classList.remove("bg-animate");
      }
    };
  }, []);


  useEffect(() => {
    if (!activeArtwork?.id) return;
    if (ratedById.has(activeArtwork.id)) {
      setRatingHeaderText("You’ve rated this artwork. Want to make a change?");
      return;
    }
    setRatingHeaderText(
      ratingHeaders[Math.floor(Math.random() * ratingHeaders.length)]
    );
  }, [activeArtwork?.id, ratedById]);

  useEffect(() => {
    if (view !== "details") return;
    const id = activeArtwork?.id;
    if (!id || id === "loading") return;
    if (requestedRatingsRef.current.has(id)) return;
    requestedRatingsRef.current.add(id);
    (async () => {
      const summary = await fetchRatingsSummary([id]);
      if (!summary.size) return;
      startTransition(() => {
        setArtworksData((prev) => applyRatingsSummaryToItems(prev, summary));
        setCollectionItems((prev) => applyRatingsSummaryToItems(prev, summary));
      });
    })();
  }, [view, activeArtwork?.id]);

  useEffect(() => {
    if (view === "details") return;
    if (!pendingCollectionUpdatesRef.current.size) return;
    const updates = new Map(pendingCollectionUpdatesRef.current);
    pendingCollectionUpdatesRef.current.clear();
    runWhenIdle(() => {
      setCollectionItems((prev) => {
        let changed = false;
        const next = prev.map((item) => {
          const updated = updates.get(item.id);
          if (!updated) return item;
          changed = true;
          return updated;
        });
        return changed ? next : prev;
      });
    });
  }, [view]);

  const pageSize = 40;
  const isRankRangeFilter = collectionRanking && collectionRanking !== "unrated";
  const isGlobalRankFilter = isRankRangeFilter && USE_SUPABASE_CATALOG;
  const totalPages = Math.max(
    1,
    Math.ceil((collectionTotal || collectionItems.length) / pageSize)
  );
  const currentPage = Math.min(collectionPage, totalPages);
  const pageStart = (currentPage - 1) * pageSize;

  useEffect(() => {
    if (view !== "details") return;
    if (!artwork) return;
    if (lastViewedId.current === artwork.id) return;
    lastViewedId.current = artwork.id;

    const surveyState = getSurveyState();
    const totalViewCount = surveyState.viewCount + 1;
    localStorage.setItem("ratedArtViewCount", String(totalViewCount));
    const sessionViewCount = getSessionCount("ratedArtViewCountSession") + 1;
    setSessionCount("ratedArtViewCountSession", sessionViewCount);
  }, [view, artwork?.id]);

  useEffect(() => {
    runWhenIdle(() => {
      ensureRandomPool(INITIAL_PREFETCH_TARGET, INITIAL_PREFETCH_TARGET);
    });
  }, []);

  useEffect(() => {
    if (ENABLE_COMPARE) return;
    if (view !== "compare") return;
    setView("details");
  }, [view]);

  useEffect(() => {
    advanceSurveyQuestion(surveyQuestion);
  }, []);

  useEffect(() => {
    if (!HAS_SUPABASE) return;
    const supabase = getSupabaseBrowserClient();
    if (!supabase) return;
    let cancelled = false;
    (async () => {
      const { data, error } = await supabase
        .from("survey_questions")
        .select("question, section, sort_order, active")
        .order("section", { ascending: true })
        .order("sort_order", { ascending: true });
      if (cancelled || error || !Array.isArray(data) || !data.length) return;
      const sections = new Map();
      data.forEach((row) => {
        if (!row?.question) return;
        if (row.active === false) return;
        const title = row.section || "Questions";
        if (!sections.has(title)) sections.set(title, []);
        sections.get(title).push(row.question);
      });
      const normalizedSections = Array.from(sections.entries())
        .map(([title, questions]) => ({ title, questions }))
        .filter((section) => section.questions.length);
      const normalizedQuestions = normalizedSections.flatMap((section) => section.questions);
      if (!normalizedQuestions.length) return;
      setSurveySectionsState(normalizedSections);
      setSurveyQuestionsState(normalizedQuestions);
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    const allowed = new Set(surveyQuestionsState);
    setSeenQuestions((prev) => {
      const next = prev.filter((question) => allowed.has(question));
      if (next.length !== prev.length) {
        saveJson("ratedArtSeenQuestions", next);
      }
      return next;
    });
    setSurveyCounts(buildSurveyCountsFromResponses(surveyResponses, surveyQuestionsState));
    if (
      surveyQuestionsState.length &&
      (!surveyQuestion || !allowed.has(surveyQuestion))
    ) {
      advanceSurveyQuestion(surveyQuestion);
    }
  }, [surveyQuestionsState]);

  useEffect(() => {
    if (!showSurvey) return;
    if (surveyQuestion || !surveyQuestionsState.length) return;
    const next =
      surveyQuestionsState[Math.floor(Math.random() * surveyQuestionsState.length)];
    setSurveyQuestionState(next);
  }, [showSurvey, surveyQuestion, surveyQuestionsState]);

  useEffect(() => {
    if (view !== "compare") return;
    if (compareLeftId && compareRightId) return;
    (async () => {
      const first = await getNextArtworkId();
      if (!first) return;
      const second = await getNextArtworkId([first]);
      if (!second) return;
      setCompareLeftId(first);
      setCompareRightId(second);
    })();
  }, [view, compareLeftId, compareRightId, currentIndex, artworksList, ratedById]);

  useEffect(() => {
    if (view !== "compare") return;
    if (!compareLeftId || !compareRightId) return;
    if (compareLeftId !== compareRightId) return;
    (async () => {
      const replacement = await getNextArtworkId([compareLeftId]);
      if (replacement && replacement !== compareLeftId) {
        setCompareRightId(replacement);
      }
    })();
  }, [view, compareLeftId, compareRightId]);

  useEffect(() => {
    if (collectionPage > totalPages) {
      setCollectionPage(totalPages);
    }
  }, [collectionPage, totalPages]);

  useEffect(() => {
    if (collectionSort !== "Random") return;
    randomSortRef.current = new Map();
  }, [collectionSort, collectionPage, collectionRanking, collectionQuery]);

  useEffect(() => {
    if (!USE_SUPABASE_CATALOG && isRankRangeFilter && collectionPage !== 1) {
      setCollectionPage(1);
    }
  }, [USE_SUPABASE_CATALOG, isRankRangeFilter, collectionPage]);

  useEffect(() => {
    let isMounted = true;
    const controller = new AbortController();

    async function fetchCollectionPage(query, page, signal) {
      let useCatalog = USE_SUPABASE_CATALOG;
      const isSearchQuery = query && query !== COLLECTION_DEFAULT_QUERY;
      const effectiveSort =
        isSearchQuery && collectionSort === "Random (Global)"
          ? "Relevance"
          : collectionSort;
      const cacheKey = JSON.stringify({
        q: query,
        ranking: collectionRanking,
        sort: effectiveSort
      });
      const state = collectionCache.current.get(cacheKey) || {
        pages: new Map(),
        buffer: [],
        nextOffset: 0,
        total: 0
      };

      if (state.pages.has(page)) {
        collectionCache.current.set(cacheKey, state);
        return { items: state.pages.get(page), total: state.total };
      }

      if (useCatalog) {
        if (!collectionRanking && !isSearchQuery && collectionSort === "Random (Global)") {
          let totalCount = Number(collectionTotal || 0);
          if (!totalCount) {
            const probe = await fetchCatalogPage({
              q: query,
              limit: 1,
              offset: 0,
              sort: "Relevance",
              isDefault: query === COLLECTION_DEFAULT_QUERY
            });
            if (!probe.ok || !probe.total) {
              useCatalog = false;
            } else {
              totalCount = Number(probe.total || 0);
            }
          }
          if (!useCatalog) {
            // fall through to Met-backed search
          } else {
          const windowSize = Math.min(10, pageSize);
          const windowCount = Math.max(1, Math.ceil(pageSize / windowSize));
          const offsets = Array.from({ length: windowCount }, () => {
            if (totalCount <= windowSize) return 0;
            return Math.floor(Math.random() * (totalCount - windowSize));
          });
          const batches = await Promise.all(
            offsets.map((offset) =>
              fetchCatalogPage({
                q: query,
                limit: windowSize,
                offset,
                sort: "Relevance",
                isDefault: query === COLLECTION_DEFAULT_QUERY
              })
            )
          );
          const combined = shuffleArray(
            dedupeCollectionItems(batches.flatMap((batch) => batch.items || []))
          ).slice(0, pageSize);
          if (!combined.length) {
            useCatalog = false;
          } else {
          const normalized = combined.map((item) => {
            const baseDistribution = buildEmptyDistribution();
            return {
              id: item.id,
              title: item.title || "Untitled",
              artist: item.artist || "Unknown",
              date: item.date || "",
              dateBegin: Number.isFinite(item.dateBegin) ? item.dateBegin : null,
              dateEnd: Number.isFinite(item.dateEnd) ? item.dateEnd : null,
              medium: item.medium || "",
              objectName: item.objectName || "",
              culture: item.culture || "",
              creditLine: item.creditLine || "",
              accessionNumber: item.accessionNumber || "",
              objectNumber: item.objectNumber || "",
              period: item.period || "",
              classification: item.classification || "",
              artistDisplayBio: item.artistDisplayBio || "",
              description: item.description || "",
              imageSmall: item.imageSmall || item.imageLarge,
              imageLarge: item.imageLarge || item.imageSmall,
              distribution: buildEmptyDistribution(),
              __baseDistribution: baseDistribution
            };
          });
          state.pages.set(page, normalized);
          state.total = totalCount;
          collectionCache.current.set(cacheKey, state);
          return { items: normalized, total: state.total };
          }
          }
        }
        if (useCatalog && collectionRanking) {
          let ratingsMap = await fetchGlobalRatingsSummaryAll(userRatingsById);
          if (!ratingsMap.size && typeof window !== "undefined") {
            if (window.__globalRatingsCache) {
              window.__globalRatingsCache.fetchedAt = 0;
            }
            ratingsMap = await fetchGlobalRatingsSummaryAll(userRatingsById);
          }
        if (!ratingsMap.size) {
          return { items: [], total: 0 };
        }
          if (collectionRanking === "unrated") {
            const ratedIds = new Set(Array.from(ratingsMap.keys()).map(Number));
            const targetCount = pageSize;
            const maxBatches = 6;
            let scanOffset = 0;
            let total = 0;
            const collected = [];
            for (let i = 0; i < maxBatches && collected.length < targetCount; i += 1) {
              const batch = await fetchCatalogPage({
                q: query,
                limit: pageSize * 2,
                offset: scanOffset,
                sort: effectiveSort,
                isDefault: query === COLLECTION_DEFAULT_QUERY
              });
              if (!batch.ok && query === COLLECTION_DEFAULT_QUERY) {
                useCatalog = false;
                break;
              }
              total = batch.total || total;
              scanOffset += batch.items.length;
              if (!batch.items.length) break;
              const normalized = batch.items
                .map((item) => {
                  const baseDistribution = buildEmptyDistribution();
                  return {
                    id: item.id,
                    title: item.title || "Untitled",
                    artist: item.artist || "Unknown",
                    date: item.date || "",
                    dateBegin: Number.isFinite(item.dateBegin) ? item.dateBegin : null,
                    dateEnd: Number.isFinite(item.dateEnd) ? item.dateEnd : null,
                    medium: item.medium || "",
                    objectName: item.objectName || "",
                    culture: item.culture || "",
                    creditLine: item.creditLine || "",
                    accessionNumber: item.accessionNumber || "",
                    objectNumber: item.objectNumber || "",
                    period: item.period || "",
                    classification: item.classification || "",
                    artistDisplayBio: item.artistDisplayBio || "",
                    description: item.description || "",
                    imageSmall: item.imageSmall || item.imageLarge,
                    imageLarge: item.imageLarge || item.imageSmall,
                    distribution: buildEmptyDistribution(),
                    __baseDistribution: baseDistribution
                  };
                })
                .filter((item) => !ratedIds.has(item.id));
              normalized.forEach((item) => {
                if (collected.length < targetCount) collected.push(item);
              });
            }
            state.pages.set(page, collected);
            state.total = collected.length || total;
            collectionCache.current.set(cacheKey, state);
            return { items: collected, total: state.total };
          }

          const [min, max] = collectionRanking.split("-").map(Number);
          const scored = [];
          ratingsMap.forEach((distribution, id) => {
            const average = computeOverallAverageFromDistributionSparse(distribution);
            if (!average) return;
            if (Number.isNaN(min) || Number.isNaN(max)) return;
            if (average >= min && average <= max) {
              scored.push({ id: Number(id), average });
            }
          });
          scored.sort((a, b) => b.average - a.average);
          const total = scored.length;
          const start = (page - 1) * pageSize;
          const pageIds = scored.slice(start, start + pageSize).map((item) => item.id);
          const fetched = await fetchArtworksServer(pageIds);
          const fetchedIds = new Set(fetched.map((item) => item.id));
          const missingIds = pageIds.filter((id) => !fetchedIds.has(id));
          let fallbackItems = [];
          if (missingIds.length) {
            fallbackItems = await fetchMetObjectsWithRetries(missingIds, {
              batchSize: DETAILS_BATCH_SIZE,
              concurrency: 4
            });
          }
          const mergedFetched = [...fetched, ...fallbackItems];
          const normalized = mergedFetched.map((item) => {
            const baseDistribution = buildEmptyDistribution();
            return {
              id: item.id,
              title: item.title || "Untitled",
              artist: item.artist || "Unknown",
              date: item.date || "",
              dateBegin: Number.isFinite(item.dateBegin) ? item.dateBegin : null,
              dateEnd: Number.isFinite(item.dateEnd) ? item.dateEnd : null,
              medium: item.medium || "",
              objectName: item.objectName || "",
              culture: item.culture || "",
              creditLine: item.creditLine || "",
              accessionNumber: item.accessionNumber || "",
              objectNumber: item.objectNumber || "",
              period: item.period || "",
              classification: item.classification || "",
              artistDisplayBio: item.artistDisplayBio || "",
              description: item.description || "",
              imageSmall: item.imageSmall || item.imageLarge,
              imageLarge: item.imageLarge || item.imageSmall,
              distribution: buildEmptyDistribution(),
              __baseDistribution: baseDistribution
            };
          });
          const summary = new Map();
          pageIds.forEach((id) => {
            const dist = ratingsMap.get(String(id));
            if (dist) summary.set(String(id), dist);
          });
          const merged = applyRatingsSummaryToItems(normalized, summary);
          state.pages.set(page, merged);
          state.total = total;
          collectionCache.current.set(cacheKey, state);
          return { items: merged, total };
        }
        if (useCatalog) {
          const offset = (page - 1) * pageSize;
          const data = await fetchCatalogPage({
          q: query,
          limit: pageSize,
          offset,
          sort: effectiveSort,
          isDefault: query === COLLECTION_DEFAULT_QUERY
          });
          if (!data.ok) {
            useCatalog = false;
          } else {
            if (!data.items?.length && query !== COLLECTION_DEFAULT_QUERY) {
              useCatalog = false;
            } else {
            const normalized = data.items.map((item) => {
          const baseDistribution = buildEmptyDistribution();
          return {
            id: item.id,
            title: item.title || "Untitled",
            artist: item.artist || "Unknown",
            date: item.date || "",
            dateBegin: Number.isFinite(item.dateBegin) ? item.dateBegin : null,
            dateEnd: Number.isFinite(item.dateEnd) ? item.dateEnd : null,
            medium: item.medium || "",
            objectName: item.objectName || "",
            culture: item.culture || "",
            creditLine: item.creditLine || "",
            accessionNumber: item.accessionNumber || "",
            objectNumber: item.objectNumber || "",
            period: item.period || "",
            classification: item.classification || "",
            artistDisplayBio: item.artistDisplayBio || "",
            description: item.description || "",
            imageSmall: item.imageSmall || item.imageLarge,
            imageLarge: item.imageLarge || item.imageSmall,
            distribution: buildEmptyDistribution(),
            __baseDistribution: baseDistribution
            };
          });
          state.pages.set(page, normalized);
          state.total = data.total || state.total;
          collectionCache.current.set(cacheKey, state);
          return { items: normalized, total: state.total };
            }
          }
        }
      }

      const batchSize = pageSize * 3;
      let attempts = 0;
      const maxAttempts = 20;

      while (!state.pages.has(page) && attempts < maxAttempts) {
        let data = await getMetSearchIds({
          q: query,
          limit: batchSize,
          offset: state.nextOffset,
          random: false
        });
        if (!data.objectIDs || !data.objectIDs.length) {
          data = await getMetSearchIds({
            q: collectionQuery || COLLECTION_DEFAULT_QUERY,
            limit: batchSize,
            offset: state.nextOffset,
            random: false
          });
        }
        if (!state.total && typeof data.total === "number") {
          state.total = data.total;
        }

        const rawIds = Array.isArray(data.objectIDs) ? data.objectIDs : [];
        const ids = dedupeIds(
          selectIdsFromSearch(rawIds.map(String), {
            limit: batchSize,
            offset: state.nextOffset,
            random: false
          })
        );
        if (!ids.length) {
          break;
        }
        state.nextOffset += ids.length;

        let items = await fetchMetObjectsWithRetries(ids, {
          concurrency: 6,
          batchSize: DETAILS_BATCH_SIZE
        });
        const normalized = Array.from(
          new Map(items.map((item) => [item.id, item])).values()
        ).map((item) => {
          const baseDistribution = buildEmptyDistribution();
          return {
            id: item.id,
            title: item.title || "Untitled",
            artist: item.artist || "Unknown",
            date: item.date || "",
            dateBegin: Number.isFinite(item.dateBegin)
              ? item.dateBegin
              : Number.isFinite(item.objectBeginDate)
                ? item.objectBeginDate
                : null,
            dateEnd: Number.isFinite(item.dateEnd)
              ? item.dateEnd
              : Number.isFinite(item.objectEndDate)
                ? item.objectEndDate
                : null,
            medium: item.medium || "",
            objectName: item.objectName || "",
            culture: item.culture || "",
            creditLine: item.creditLine || "",
            accessionNumber: item.accessionNumber || "",
            objectNumber: item.objectNumber || "",
            period: item.period || "",
            classification: item.classification || "",
            artistDisplayBio: item.artistDisplayBio || "",
            description: item.description || "",
            imageSmall: item.imageSmall || item.imageLarge,
            imageLarge: item.imageLarge || item.imageSmall,
            distribution: buildEmptyDistribution(),
            __baseDistribution: baseDistribution
          };
        });

        state.buffer.push(...normalized);
        while (state.buffer.length >= pageSize) {
          const nextPageNumber = state.pages.size + 1;
          const pageItems = state.buffer.splice(0, pageSize);
          state.pages.set(nextPageNumber, pageItems);
        }

        attempts += 1;
      }

      collectionCache.current.set(cacheKey, state);
      return { items: state.pages.get(page) || [], total: state.total };
    }

    async function prefetchNextPage(query, page) {
      const nextPage = page + 1;
      const cacheKey = JSON.stringify({
        q: query,
        ranking: collectionRanking,
        sort: collectionSort
      });
      const state = collectionCache.current.get(cacheKey);
      if (state?.pages?.has(nextPage)) return;
      try {
        await fetchCollectionPage(query, nextPage, controller.signal);
      } catch (error) {
        if (error.name !== "AbortError") {
          console.error(error);
        }
      }
    }

    async function loadCollectionPage() {
      setCollectionLoading(true);
      try {
        const query = buildCollectionQueryTerm(collectionQuery);
        const data = await fetchCollectionPage(
          query,
          collectionPage,
          controller.signal
        );
        if (isMounted) {
          if (data.total) setCollectionTotal(data.total);
          const hydrated = applyStoredRatingsToItems(data.items);
          startTransition(() => {
            setCollectionItems(hydrated);
          });
          const summary = await fetchRatingsSummary(hydrated.map((item) => item.id));
          startTransition(() => {
            setCollectionItems((prev) => applyRatingsSummaryToItems(prev, summary));
          });
        }
        prefetchNextPage(query, collectionPage);
      } catch (error) {
        if (error.name !== "AbortError") {
          console.error(error);
        }
      } finally {
        if (isMounted) {
          setCollectionLoading(false);
        }
      }
    }

    loadCollectionPage();
    return () => {
      isMounted = false;
      controller.abort();
    };
  }, [collectionQuery, collectionPage, collectionSort, collectionRanking, collectionRefresh]);

  useEffect(() => {
    if (!collectionRanking) return;
    if (globalSyncStatus !== "synced") return;
    collectionCache.current = new Map();
    setCollectionRefresh((prev) => prev + 1);
  }, [collectionRanking, globalSyncStatus]);

  useEffect(() => {
    let isMounted = true;
    const controller = new AbortController();

    async function loadArtworks() {
      try {
        setAppLoading(true);
        setAppLoadError("");
        setAppLoadProgress(0);
        setAppLoadTotal(0);
        const initialBatch = 48;
        const poolParams = { q: MET_SAMPLER_QUERY };
        const warmupData = await getMetSearchIds({
          q: MET_SAMPLER_QUERY,
          limit: initialBatch * 4,
          random: true
        });
        const warmupIds = Array.isArray(warmupData.objectIDs) ? warmupData.objectIDs : [];
        let total = totalResults || collectionTotal || Number(warmupData.total || 0);
        if (total) setTotalResults(total);
        const safeMax = Math.max(0, total - initialBatch);
        let offset = safeMax ? Math.floor(Math.random() * safeMax) : 0;
        let aggregatedResults = [];
        let aggregatedTotal = Number(warmupData.total || 0);
        let attempts = 0;
        const uniqueIds = new Set();
        const excludeSet = new Set(seenArtworkIds.current);
        let uniqueCount = 0;
        const pickWarmupIds = (limit) => {
          if (!warmupIds.length) return [];
          const candidates = warmupIds.filter((id) => !excludeSet.has(id));
          if (!candidates.length) return [];
          const target = Math.min(limit, candidates.length);
          const picked = new Set();
          while (picked.size < target) {
            picked.add(candidates[Math.floor(Math.random() * candidates.length)]);
          }
          return Array.from(picked);
        };
        while (uniqueCount < initialBatch && attempts < 8) {
          const ids = await pickIdsFromPool(poolParams, initialBatch, excludeSet);
          let selectedIds = ids;
          if (!selectedIds.length) {
            selectedIds = pickWarmupIds(initialBatch);
          }
          if (!selectedIds.length) break;
          selectedIds.forEach((id) => excludeSet.add(id));
          const cachedLocal = selectedIds.map((id) => getCachedObject(id)).filter(Boolean);
          const cachedDb = await getCachedObjects(selectedIds);
          const cached = [
            ...cachedLocal,
            ...selectedIds.map((id) => cachedDb.get(id)).filter(Boolean)
          ];
          const cachedIds = new Set(cached.map((item) => String(item.id)));
          if (cached.length) setAppLoadProgress(cached.length);
          const missingIds = selectedIds.filter((id) => !cachedIds.has(id));
          let fetched = [];
          if (missingIds.length) {
            fetched = await fetchMetObjectsWithRetries(missingIds, {
              concurrency: 6,
              batchSize: DETAILS_BATCH_SIZE,
              onProgress: (count) => setAppLoadProgress(cached.length + count)
            });
          }
          aggregatedResults = [...aggregatedResults, ...cached, ...fetched];
          aggregatedResults.forEach((item) => {
            if (item?.id) uniqueIds.add(item.id);
          });
          uniqueCount = uniqueIds.size;
          attempts += 1;
          setAppLoadProgress(aggregatedResults.length);
          if (safeMax) {
            offset = Math.floor(Math.random() * safeMax);
          }
        }

        if (aggregatedResults.length < initialBatch) {
          let ids = await pickIdsFromPool(poolParams, initialBatch, excludeSet);
          if (!ids.length) {
            ids = pickWarmupIds(initialBatch);
          }
          if (ids.length) {
            let fetched = await fetchMetObjectsWithRetries(ids, {
              concurrency: 6,
              batchSize: DETAILS_BATCH_SIZE,
              onProgress: (count) => setAppLoadProgress(count)
            });
            aggregatedResults = [...aggregatedResults, ...fetched];
          }
        }

        const dedupedResults = Array.from(
          new Map(aggregatedResults.map((item) => [item.id, item])).values()
        );
        if (!dedupedResults.length) {
          if (isMounted) {
            setAppLoadError("Unable to load artworks right now.");
            setAppLoading(false);
          }
          return;
        }

        if (aggregatedTotal && isMounted) {
          setTotalResults(aggregatedTotal);
        }
        const normalized = dedupedResults.map((item) => {
          const baseDistribution = buildEmptyDistribution();
          return {
            id: item.id,
            title: item.title || "Untitled",
            artist: item.artist || "Unknown",
            date: item.date || "",
            dateBegin: Number.isFinite(item.dateBegin)
              ? item.dateBegin
              : Number.isFinite(item.objectBeginDate)
                ? item.objectBeginDate
                : null,
            dateEnd: Number.isFinite(item.dateEnd)
              ? item.dateEnd
              : Number.isFinite(item.objectEndDate)
                ? item.objectEndDate
                : null,
            medium: item.medium || "",
            objectName: item.objectName || "",
            culture: item.culture || "",
            creditLine: item.creditLine || "",
            accessionNumber: item.accessionNumber || "",
            objectNumber: item.objectNumber || "",
            period: item.period || "",
            classification: item.classification || "",
            artistDisplayBio: item.artistDisplayBio || "",
            description: item.description || "",
            imageSmall: item.imageSmall || item.imageLarge,
            imageLarge: item.imageLarge || item.imageSmall,
            distribution: buildEmptyDistribution(),
            __baseDistribution: baseDistribution
          };
        });

        if (isMounted) {
          const hydrated = applyStoredRatingsToItems(normalized);
          registerArtworks(hydrated);
          const shuffled = hydrated.sort(() => Math.random() - 0.5);
          startTransition(() => {
            setArtworksData(shuffled);
          });
          const summary = await fetchRatingsSummary(shuffled.map((item) => item.id));
          startTransition(() => {
            setArtworksData((prev) => applyRatingsSummaryToItems(prev, summary));
          });
          setCurrentIndex(0);
          setAppLoading(false);
        }
      } catch (error) {
        if (error.name !== "AbortError") {
          console.error(error);
          if (isMounted) {
            setAppLoadError("Unable to load artworks right now.");
            setAppLoading(false);
          }
        }
      }
    }

    loadArtworks();
    return () => {
      isMounted = false;
      controller.abort();
    };
  }, []);

  async function enqueueRandomArtworks(batchCount = 24) {
    if (randomLoading.current) return;
    randomLoading.current = true;
    try {
    const query = buildCollectionQueryTerm(collectionQuery);
      const poolParams = { q: query };
      const warmupData = await getMetSearchIds({
        q: query,
        limit: batchCount,
        random: true
      });
      const warmupIds = Array.isArray(warmupData.objectIDs) ? warmupData.objectIDs : [];
      let total = totalResults || collectionTotal || Number(warmupData.total || 0);
      if (total) setTotalResults(total);

      if (!total) return;
      const excludeIds = new Set([
        ...artworksList.map((art) => art.id),
        ...randomPool.current.map((art) => art.id),
        ...seenArtworkIds.current
      ]);
      const ids = await pickIdsFromPool(poolParams, batchCount, excludeIds);
      let selectedIds = ids;
      if (!selectedIds.length && warmupIds.length) {
        const candidates = warmupIds.filter((id) => !excludeIds.has(id));
        if (candidates.length) {
          const target = Math.min(batchCount, candidates.length);
          const picked = new Set();
          while (picked.size < target) {
            picked.add(candidates[Math.floor(Math.random() * candidates.length)]);
          }
          selectedIds = Array.from(picked);
        }
      }
      if (!selectedIds.length) return;

      const fetchConcurrency = allowBackgroundWork ? 6 : 2;
      const fetchBatchSize = allowBackgroundWork ? DETAILS_BATCH_SIZE : 12;
      let items = await fetchMetObjectsWithRetries(selectedIds, {
        concurrency: fetchConcurrency,
        batchSize: fetchBatchSize
      });
      await new Promise((resolve) => setTimeout(resolve, 0));
      const normalized = await normalizeArtworks(items);

      const hydrated = applyStoredRatingsToItems(normalized);
      let merged = hydrated;
      if (allowBackgroundWork) {
        const summary = await fetchRatingsSummary(hydrated.map((item) => item.id));
        await new Promise((resolve) => setTimeout(resolve, 0));
        merged = applyRatingsSummaryToItems(hydrated, summary);
      }
      registerArtworks(merged);
      const existing = new Set([
        ...artworksList.map((art) => art.id),
        ...randomPool.current.map((art) => art.id),
        ...seenArtworkIds.current
      ]);
      merged.forEach((item) => {
        if (!existing.has(item.id)) {
          randomPool.current.push(item);
        }
      });
    } catch (error) {
      console.error(error);
    } finally {
      randomLoading.current = false;
    }
  }

  function markSeenArtwork(id) {
    if (!id || id === "loading") return;
    if (seenArtworkIds.current.has(id)) return;
    seenArtworkIds.current.add(id);
    randomPool.current = randomPool.current.filter((art) => art.id !== id);
    persistSeenArtworkIds();
  }

  async function handleRateClick() {
    setView("details");
    setFromCollection(false);
    setShowSurvey(false);
    historyIdsRef.current = [];
    historyIndexRef.current = -1;
    setHistoryIds([]);
    setHistoryIndex(-1);
    let index = await pickRandomArtworkIndex();
    if (index === null) {
      runWhenIdle(() => {
        void enqueueRandomArtworks(RATING_PREFETCH_TARGET);
      });
      index = await pickRandomArtworkIndex();
    }
    if (index !== null) {
      setCurrentIndex(index);
    }
  }

  async function handleHomeClick() {
    setView("details");
    setFromCollection(false);
    surveyAdvanceCountRef.current = 0;
    advanceSurveyQuestion(surveyQuestion);
    setShowSurvey(true);
    let index = await pickRandomArtworkIndex();
    if (index === null) {
      runWhenIdle(() => {
        void enqueueRandomArtworks(RATING_PREFETCH_TARGET);
      });
      index = await pickRandomArtworkIndex();
    }
    if (index !== null) {
      setCurrentIndex(index);
    }
  }


  function handleStarClick(criterion, value) {
    setDraftRatings((prev) => ({
      ...prev,
      [activeArtwork.id]: { ...(prev[activeArtwork.id] || {}), [criterion]: value }
    }));
  }

  function applyUserRating(artId, scores, previousScores) {
    const updateDistribution = (art) => {
      if (!art || art.id !== artId) return art;
      const updated = updateArtworkDistribution(art, scores, previousScores);
      updateCollectionCacheItem(updated);
      return updated;
    };

    if (view === "details") {
      startTransition(() => {
        setArtworksData((prev) => {
          const index = prev.findIndex((art) => art.id === artId);
          if (index === -1) return prev;
          const next = prev.slice();
          next[index] = updateDistribution(prev[index]);
          return next;
        });
      });
      const cached = artworkById.current.get(artId);
      if (cached) {
        pendingCollectionUpdatesRef.current.set(artId, cached);
      }
      return;
    }
    startTransition(() => {
      setArtworksData((prev) => prev.map(updateDistribution));
      setCollectionItems((prev) => prev.map(updateDistribution));
    });
  }

  function saveRating() {
    if (!isComplete) return;
    const previousScores = userRatingsById[activeArtwork.id];
    const updated = new Set(ratedById);
    updated.add(activeArtwork.id);
    setRatedById(updated);
    saveJson("ratedArtRatedIds", Array.from(updated));
    const nextRatings = {
      ...userRatingsById,
      [activeArtwork.id]: { ...draftScores }
    };
    setUserRatingsById(nextRatings);
    saveJson("ratedArtUserRatings", nextRatings);
    setRatingModeById((prev) => {
      const next = new Set(prev);
      next.delete(activeArtwork.id);
      return next;
    });
    applyUserRating(activeArtwork.id, draftScores, previousScores);

    const surveyState = getSurveyState();
    const nextCount = surveyState.ratingCount + (previousScores ? 0 : 1);
    localStorage.setItem("ratedArtRatingCount", String(nextCount));

    void postEvent("/api/ratings", {
      artworkId: activeArtwork.id,
      scores: { ...draftScores },
      clientId: getClientId()
    }).then((result) => {
      if (!result?.ok) return;
      if (typeof window !== "undefined" && window.__globalRatingsCache) {
        const cache = window.__globalRatingsCache;
        const key = String(activeArtwork.id);
        if (cache.map && !cache.map.has(key)) {
          const distribution = buildDistributionFromScores(draftScores);
          if (Object.keys(distribution).length) {
            cache.map.set(key, distribution);
          }
        }
        cache.fetchedAt = 0;
      }
      const parsed = Number(result.data?.totalRatedArtworks);
      if (Number.isFinite(parsed)) {
        setGlobalRatedArtworks(parsed);
        saveJson(GLOBAL_RATED_KEY, parsed);
        setGlobalSyncStatus("synced");
        return;
      }
      void refreshGlobalStats({ refreshSurvey: false, refreshRatings: true });
    });

    const remainingUnrated = artworksList.some((art) => !updated.has(art.id));
    if (!remainingUnrated) {
      void loadFreshRandomBatch(24);
    }
  }

  function recordSurveyResponse(answer) {
    const resolvedQuestion = surveyQuestion || surveyQuestionsState[0];
    if (!resolvedQuestion) return;
    const entry = {
      question: resolvedQuestion,
      answer,
      createdAt: new Date().toISOString()
    };
    setSurveyResponses((prev) => {
      const next = [...prev, entry];
      saveJson("ratedArtSurveyResponses", next);
      return next;
    });
    setSurveyCounts((prev) => incrementSurveyCounts(prev, resolvedQuestion, answer));
    setSeenQuestions((prev) => {
      if (prev.includes(resolvedQuestion)) return prev;
      const next = [...prev, resolvedQuestion];
      saveJson("ratedArtSeenQuestions", next);
      return next;
    });

    void postEvent("/api/survey", {
      question: resolvedQuestion,
      answer,
      source: "home",
      artworkId: activeArtwork?.id ?? null,
      clientId: getClientId()
    }).then((result) => {
      if (result?.ok) {
        void refreshGlobalStats({ refreshSurvey: true, refreshRatings: false });
      }
    });

    surveyAnswerCountRef.current += 1;
    if (surveyAnswerCountRef.current >= 2) {
      surveyAnswerCountRef.current = 0;
      void transitionToAvailableArtworkFromSurvey();
      return;
    }
    advanceSurveyQuestion(resolvedQuestion);
    setView("details");
  }

  function handleSurveyAnswer(answer, event) {
    const now = Date.now();
    if (event?.type?.startsWith?.("touch")) {
      event.preventDefault();
      event.stopPropagation();
      lastSurveyInputRef.current = now;
      recordSurveyResponse(answer);
      return;
    }
    if (now - lastSurveyInputRef.current < 500) return;
    recordSurveyResponse(answer);
  }

  function getNextSurveyQuestion(excludeQuestion = null) {
    if (!surveyQuestionsState.length) return null;
    const unseen = surveyQuestionsState.filter((question) => !seenQuestions.includes(question));
    let pool = unseen.length ? unseen : surveyQuestionsState;
    if (excludeQuestion && pool.length > 1) {
      pool = pool.filter((question) => question !== excludeQuestion);
    }
    return pool[Math.floor(Math.random() * pool.length)];
  }

  function advanceSurveyQuestion(excludeQuestion = null) {
    const next = getNextSurveyQuestion(excludeQuestion);
    if (!next) return;
    setSurveyQuestionState(next);
    setShowSurvey(true);
  }

  function recordQuestionResponse(answer) {
    if (!surveyQuestionsState.length) return;
    const question =
      surveyQuestionsState[questionIndex % surveyQuestionsState.length];
    const entry = {
      question,
      answer,
      createdAt: new Date().toISOString()
    };
    const nextResponses = [...surveyResponses, entry];
    const nextCounts = incrementSurveyCounts(surveyCounts, question, answer);
    setSurveyResponses(() => {
      saveJson("ratedArtSurveyResponses", nextResponses);
      return nextResponses;
    });
    setSurveyCounts(nextCounts);
    setSeenQuestions((prev) => {
      if (prev.includes(question)) return prev;
      const next = [...prev, question];
      saveJson("ratedArtSeenQuestions", next);
      return next;
    });

    const nextSeen = seenQuestions.includes(question)
      ? seenQuestions
      : [...seenQuestions, question];
    void postEvent("/api/survey", {
      question,
      answer,
      source: "questions",
      clientId: getClientId()
    }).then((result) => {
      if (result?.ok) {
        void refreshGlobalStats({ refreshSurvey: true, refreshRatings: false });
      }
    });

    setQuestionIndex(
      pickQuestionIndexByCounts(nextCounts, question, nextSeen, surveyQuestionsState)
    );
  }

  function renderBars() {
    return (
      <div className="reviews-summary">
        <div className="reviews-bars">
          {(() => {
            const average = totalReviews ? computeAverage(compositeDistribution) : 0;
            const rounded = Math.max(1, Math.min(5, Math.round(average || 0)));
            return [5, 4, 3, 2, 1].map((level) => {
              const percent = totalReviews && level === rounded ? 100 : 0;
              return (
                <div key={level} className="reviews-row">
                  <div className="reviews-label">{level}</div>
                  <div className="reviews-bar">
                    <div
                      className="reviews-fill"
                      style={{ width: `${percent}%` }}
                    />
                  </div>
                </div>
              );
            });
          })()}
          {ratedById.has(activeArtwork.id) ? (
            <div className="user-review-summary">
            <h3 className="user-review-title">Your Review</h3>
              <div className="user-review-grid">
                {criteria.map((criterion) => (
                  <div key={criterion} className="user-review-row">
                    <span>{criterion}</span>
                    <span
                      className="user-review-stars"
                      style={{
                        "--percent": `${Math.min(
                          100,
                          Math.max(0, ((userScores[criterion] || 0) / 5) * 100)
                        )}%`
                      }}
                      aria-hidden="true"
                    >
                      ★★★★★
                    </span>
                  </div>
                ))}
              </div>
            </div>
          ) : null}
          <button
            className="button button-outline reviews-cta"
            onClick={() => {
              setRatingModeById((prev) => new Set([...prev, activeArtwork.id]));
            }}
          >
            Rate this artwork
          </button>
        </div>
        <div className="reviews-score">
          <div className="reviews-average">
            {totalReviews ? computeAverage(compositeDistribution).toFixed(1) : "–"}
          </div>
          <div
            className="reviews-stars"
            style={{
              "--percent": totalReviews
                ? `${Math.min(
                    100,
                    Math.max(0, (computeAverage(compositeDistribution) / 5) * 100)
                  )}%`
                : "0%"
            }}
            aria-hidden="true"
          >
            ★★★★★
          </div>
          <div className="reviews-subtitle">
            {totalReviews} review{totalReviews === 1 ? "" : "s"}
          </div>
          {totalReviews ? (
            <div className="reviews-confidence">
              LCB: {formatBayesLcb(activeArtwork)} · {getConfidenceLabel(activeArtwork)}
            </div>
          ) : null}
        </div>
      </div>
    );
  }

  function renderRating() {
    return (
        <div className="inline-rating">
        <div className="inline-rating-header">{ratingHeaderText}</div>
        {criteria.map((criterion) => {
          const selected = draftScores[criterion] || 0;
          return (
            <div key={criterion} className="rating-row">
              <div className="scale-row">
                <div className="rating-row-title">
                  <span className="tooltip-trigger">
                    {criterion}
                    <span className="tooltip">{criteriaHelp[criterion]}</span>
                  </span>
                </div>
                <div className="rating-stars">
                  {[5, 4, 3, 2, 1].map((value) => (
                    <button
                      key={value}
                      className={`rating-star ${selected >= value ? "is-active" : ""}`}
                      type="button"
                      onClick={() => handleStarClick(criterion, value)}
                    >
                      ★
                    </button>
                  ))}
                </div>
              </div>
            </div>
          );
        })}
        <div className="action-bar">
          <button
            className="button button-outline"
            disabled={!isComplete}
            onClick={saveRating}
          >
            Save review
          </button>
        </div>
      </div>
    );
  }

  function renderCompare() {
    if (!artworksList.length) {
      return (
        <section className="attention compare-view">
          <div className="attention-question">Loading artworks…</div>
        </section>
      );
    }

    if (!compareLeftId || !compareRightId) return null;
    const leftArtwork = getArtworkById(compareLeftId);
    const rightArtwork = getArtworkById(compareRightId);
    if (!leftArtwork || !rightArtwork) {
      (async () => {
        const first = await getNextArtworkId();
        if (!first) return;
        const second = await getNextArtworkId([first]);
        if (!second) return;
        setCompareLeftId(first);
        setCompareRightId(second);
      })();
      return null;
    }

    return (
      <section className="attention compare-view">
        <div className="attention-question">Which is the stronger piece of art?</div>
        <div className="attention-grid">
          <div className="compare-slot">
            <button
              className="attention-card"
              onClick={async () => {
                setCompareStreak((prev) => ({
                  id: leftArtwork.id,
                  count: prev.id === leftArtwork.id ? prev.count + 1 : 1
                }));
                const replacement = await getNextArtworkId([leftArtwork.id, rightArtwork.id]);
                if (replacement && replacement !== rightArtwork.id) {
                  setCompareRightId(replacement);
                }
              }}
            >
              <div className={`image-shell ${loadedImages.has(leftArtwork.id) ? "is-loaded" : ""}`}>
                <img
                src={proxyImageUrl(leftArtwork.imageSmall || leftArtwork.imageLarge, `${leftArtwork.id}-small`, 600)}
                  srcSet={
                    imageQuality === "high" && leftArtwork.imageSmall && leftArtwork.imageLarge
                      ? `${proxyImageUrl(leftArtwork.imageSmall, `${leftArtwork.id}-small`, 600)} 600w, ${proxyImageUrl(leftArtwork.imageLarge, `${leftArtwork.id}-large`, 1400)} 1400w`
                      : undefined
                  }
                  sizes="(max-width: 1200px) 40vw, 30vw"
                  alt={leftArtwork.title}
                  loading="eager"
                  decoding="async"
                  fetchPriority="low"
                  onLoad={() => markImageLoaded(leftArtwork.id)}
                />
              </div>
            </button>
            {compareStreak.id === leftArtwork.id ? (
              <div className="streak-counter">Streak: {compareStreak.count}</div>
            ) : (
              <div className="streak-counter is-empty"></div>
            )}
          </div>
          <div className="compare-slot">
            <button
              className="attention-card"
              onClick={async () => {
                setCompareStreak((prev) => ({
                  id: rightArtwork.id,
                  count: prev.id === rightArtwork.id ? prev.count + 1 : 1
                }));
                const replacement = await getNextArtworkId([rightArtwork.id, leftArtwork.id]);
                if (replacement && replacement !== leftArtwork.id) {
                  setCompareLeftId(replacement);
                }
              }}
            >
              <div className={`image-shell ${loadedImages.has(rightArtwork.id) ? "is-loaded" : ""}`}>
                <img
                src={proxyImageUrl(rightArtwork.imageSmall || rightArtwork.imageLarge, `${rightArtwork.id}-small`, 600)}
                  srcSet={
                    imageQuality === "high" && rightArtwork.imageSmall && rightArtwork.imageLarge
                      ? `${proxyImageUrl(rightArtwork.imageSmall, `${rightArtwork.id}-small`, 600)} 600w, ${proxyImageUrl(rightArtwork.imageLarge, `${rightArtwork.id}-large`, 1400)} 1400w`
                      : undefined
                  }
                  sizes="(max-width: 1200px) 40vw, 30vw"
                  alt={rightArtwork.title}
                  loading="eager"
                  decoding="async"
                  fetchPriority="low"
                  onLoad={() => markImageLoaded(rightArtwork.id)}
                />
              </div>
            </button>
            {compareStreak.id === rightArtwork.id ? (
              <div className="streak-counter">Streak: {compareStreak.count}</div>
            ) : (
              <div className="streak-counter is-empty"></div>
            )}
          </div>
        </div>
      </section>
    );
  }

  function renderQuestions() {
    if (!surveyQuestionsState.length) {
      return (
        <section className="survey">
          <div className="panel-text">No questions available.</div>
        </section>
      );
    }
    const question =
      surveyQuestionsState[questionIndex % surveyQuestionsState.length];
    return (
      <section className="survey">
        <button className="back-link" onClick={() => setView("results")}>
          View Results &gt;&gt;
        </button>
        <button
          className="nav-arrow left"
          onClick={() =>
            setQuestionIndex(
              (prev) =>
                (prev - 1 + surveyQuestionsState.length) % surveyQuestionsState.length
            )
          }
        >
          ‹
        </button>
        <button
          className="nav-arrow right"
          onClick={() =>
            setQuestionIndex((prev) => (prev + 1) % surveyQuestionsState.length)
          }
        >
          ›
        </button>
        <div className="survey-body">
          <div className="survey-question">{question}</div>
          <div className="survey-actions">
            <button className="button button-outline" onClick={() => recordQuestionResponse("no")}>
              No
            </button>
            <button className="button button-outline" onClick={() => recordQuestionResponse("yes")}>
              Yes
            </button>
          </div>
        </div>
      </section>
    );
  }

  function renderResults() {
    const summary = surveyQuestionsState.map((question) => {
      const counts = surveyCounts.get(question) || { yes: 0, no: 0, total: 0 };
      const total = counts.total || counts.yes + counts.no;
      return {
        question,
        yes: counts.yes,
        no: counts.no,
        total,
        yesPercent: total ? Math.round((counts.yes / total) * 100) : 0
      };
    });

    const summaryByQuestion = new Map(summary.map((item) => [item.question, item]));
    const overallTotals = summary.reduce(
      (acc, item) => ({
        yes: acc.yes + item.yes,
        no: acc.no + item.no,
        total: acc.total + item.total
      }),
      { yes: 0, no: 0, total: 0 }
    );
    const overallPercent = overallTotals.total
      ? Math.round((Math.max(overallTotals.yes, overallTotals.no) / overallTotals.total) * 100)
      : 0;
    const sectionCards = surveySectionsState.map((section) => {
      const sectionId = `survey-section-${slugifySectionId(section.title)}`;
      const totals = section.questions.reduce(
        (acc, question) => {
          const item = summaryByQuestion.get(question);
          if (!item) return acc;
          return {
            yes: acc.yes + item.yes,
            no: acc.no + item.no,
            total: acc.total + item.total
          };
        },
        { yes: 0, no: 0, total: 0 }
      );
      const percent = totals.total
        ? Math.round((Math.max(totals.yes, totals.no) / totals.total) * 100)
        : 0;
      return {
        title: section.title,
        sectionId,
        percent,
        total: totals.total
      };
    });
    return (
      <section className="survey survey-results">
        <button className="back-link" onClick={() => setView("questions")}>
          &lt;&lt; Back to Questions
        </button>
        <div className="survey-body">
          <div id="survey-results-top"></div>
          <div className="results-dashboard-wrap">
            <h1 className="results-dashboard-header">How much do we agree?</h1>
            <div className="results-dashboard">
              <div className="results-card">
                <div className="results-card-title">Overall Agreement</div>
                <div className="results-card-value">{overallPercent}%</div>
                <div className="results-card-meta">
                  {overallTotals.total ? `${overallTotals.total} responses` : "No responses yet"}
                </div>
              </div>
              {sectionCards.map((card) => (
                <button
                  key={card.title}
                  type="button"
                  className="results-card results-card-button"
                  onClick={() => {
                    const target = document.getElementById(card.sectionId);
                    if (target) {
                      target.scrollIntoView({ behavior: "smooth", block: "start" });
                    }
                  }}
                >
                  <div className="results-card-title">{card.title}</div>
                  <div className="results-card-value">{card.percent}%</div>
                  <div className="results-card-meta">
                    {card.total ? `${card.total} responses` : "No responses yet"}
                  </div>
                </button>
              ))}
            </div>
          </div>
          {summary.length ? (
            <div className="results-grid">
              {surveySectionsState.map((section) => (
                <div
                  key={section.title}
                  id={`survey-section-${slugifySectionId(section.title)}`}
                  className="results-section"
                >
                  <h1 className="results-section-title">{section.title}</h1>
                  {section.questions.map((question) => {
                    const item = summary.find((entry) => entry.question === question);
                    if (!item) return null;
                    return (
                      <div key={item.question} className="results-row">
                        <h1 className="results-question">{item.question}</h1>
                        {item.total ? (
                          <div className="results-response">
                            <div className="results-bar-row">
                              <div className="results-bar results-bar-yes">
                                <div
                                  className="results-fill yes"
                                  style={{ width: `${item.yesPercent}%` }}
                                />
                              </div>
                              <div className="results-count results-meta-yes">{item.yes}</div>
                            </div>
                            <div className="results-bar-row">
                              <div className="results-bar results-bar-no">
                                <div
                                  className="results-fill no"
                                  style={{ width: `${100 - item.yesPercent}%` }}
                                />
                              </div>
                              <div className="results-count results-meta-no">{item.no}</div>
                            </div>
                            <div className="results-meta">
                              <span className="results-meta-yes">{item.yesPercent}% yes</span> ·{" "}
                              <span className="results-meta-no">{100 - item.yesPercent}% no</span>
                            </div>
                          </div>
                        ) : (
                          <div className="results-meta">No responses yet</div>
                        )}
                      </div>
                    );
                  })}
                  <button
                    type="button"
                    className="results-back-to-top"
                    onClick={() => {
                      const target = document.getElementById("survey-results-top");
                      if (target) {
                        target.scrollIntoView({ behavior: "smooth", block: "start" });
                      }
                    }}
                  >
                    Back to Top
                  </button>
                </div>
              ))}
            </div>
          ) : (
            <div className="panel-text">No questions available.</div>
          )}
        </div>
      </section>
    );
  }

  function renderCollection() {
    const ratedItems = [...ratedById]
      .map((id) => artworkById.current.get(id))
      .filter(Boolean);
    const isSearchActive =
      collectionQuery &&
      collectionQuery.trim() &&
      collectionQuery !== COLLECTION_DEFAULT_QUERY;
    const hasGlobalRankData = collectionItems.some((item) => hasCommunityRatings(item));
    const itemsForCollection = isSearchActive
      ? collectionItems
      : isGlobalRankFilter
        ? hasGlobalRankData
          ? collectionItems
          : ratedItems
        : isRankRangeFilter
          ? ratedItems
          : collectionRanking === "unrated"
            ? collectionItems
            : ratedItems.length
              ? [...ratedItems, ...collectionItems.filter((item) => !ratedById.has(item.id))]
              : collectionItems;
    const uniqueItems = dedupeCollectionItems(itemsForCollection);
    const filteredItems = uniqueItems.filter((item) => {
      if (collectionSort === "User Ranking" && !hasCommunityRatings(item)) return false;
      if (!collectionRanking) return true;
      if (collectionRanking === "unrated") {
        return !hasCommunityRatings(item);
      }
      const [min, max] = collectionRanking.split("-").map(Number);
      const average = computeOverallAverageForItem(item);
      if (!average) return false;
      if (Number.isNaN(min) || Number.isNaN(max)) return true;
      return average >= min && average <= max;
    });
    const sortedItems = [...filteredItems];
    const getDateSortValue = (item, direction) => {
      const begin = Number.isFinite(item.dateBegin) ? item.dateBegin : null;
      const end = Number.isFinite(item.dateEnd) ? item.dateEnd : null;
      const fallback = parseDateYear(item.date);
      if (direction === "newest") return end ?? begin ?? fallback;
      return begin ?? end ?? fallback;
    };
    if (collectionSort !== "Relevance") {
      sortedItems.sort((a, b) => {
        switch (collectionSort) {
          case "User Ranking":
            return compareNumbers(
              computeOverallAverageForItem(a) || null,
              computeOverallAverageForItem(b) || null,
              "desc"
            );
          case "Random (Global)":
            return 0;
          case "Random": {
            const getKey = (item) => {
              if (!randomSortRef.current.has(item.id)) {
                randomSortRef.current.set(item.id, Math.random());
              }
              return randomSortRef.current.get(item.id);
            };
            return compareNumbers(getKey(a), getKey(b), "asc");
          }
          case "Title (a-z)":
            return compareText(a.title, b.title);
          case "Title (z-a)":
            return compareText(b.title, a.title);
          case "Date (newest-oldest)":
            return compareNumbers(getDateSortValue(a, "newest"), getDateSortValue(b, "newest"), "desc");
          case "Date (oldest-newest)":
            return compareNumbers(getDateSortValue(a, "oldest"), getDateSortValue(b, "oldest"), "asc");
          case "Artist/Maker (a-z)":
            return compareText(a.artist, b.artist);
          case "Artist/Maker (z-a)":
            return compareText(b.artist, a.artist);
          default:
            return 0;
        }
      });
    }
    const displayStart = sortedItems.length
      ? isGlobalRankFilter
        ? pageStart + 1
        : isRankRangeFilter
          ? 1
          : pageStart + 1
      : 0;
    const displayEnd = sortedItems.length
      ? isGlobalRankFilter
        ? pageStart + sortedItems.length
        : isRankRangeFilter
          ? sortedItems.length
          : pageStart + sortedItems.length
      : 0;
    const displayTotal = isGlobalRankFilter
      ? collectionTotal || sortedItems.length
      : isRankRangeFilter
        ? sortedItems.length
        : collectionTotal || collectionItems.length;
    const renderTotalPages = isGlobalRankFilter ? totalPages : isRankRangeFilter ? 1 : totalPages;

    const renderCollectionCard = (item) => (
      <button
        type="button"
        key={item.id}
        className="collection-card"
        onClick={() => {
          setShowSurvey(false);
          const index = artworksList.findIndex((art) => art.id === item.id);
          if (index !== -1) {
            setCurrentIndex(index);
          } else {
            setArtworksData((prev) => [
              item,
              ...prev.filter((art) => art.id !== item.id)
            ]);
            setCurrentIndex(0);
          }
          setFromCollection(true);
          historyIdsRef.current = [item.id];
          historyIndexRef.current = 0;
          setHistoryIds([item.id]);
          setHistoryIndex(0);
          setView("details");
        }}
      >
        <div className="collection-rank">
          {(() => {
            const count = sum(
              item.distribution?.["Overall Impression"] || [0, 0, 0, 0, 0]
            );
            const average = count ? computeOverallAverageForItem(item) : 0;
            const score = count ? average.toFixed(1) : "–";
            const percent = count
              ? Math.min(100, Math.max(0, (average / 5) * 100))
              : 0;
            return (
              <>
                {count ? (
                  <>
                    <span className="collection-score">{score}</span>
                    <span
                      className="collection-stars is-rated"
                      style={{ "--percent": `${percent}%` }}
                      aria-hidden="true"
                    >
                      ★★★★★
                    </span>
                  </>
                ) : (
                  <span className="collection-rate-me">Rate me!</span>
                )}
              </>
            );
          })()}
        </div>
        <div className="collection-thumb">
          <div className={`image-shell ${loadedImages.has(item.id) ? "is-loaded" : ""}`}>
            <LazyImage
              src={proxyImageUrl(item.imageSmall || item.imageLarge, `${item.id}-small`, 400)}
              srcSet={
                imageQuality === "high" && item.imageSmall && item.imageLarge
                  ? `${proxyImageUrl(item.imageSmall, `${item.id}-small`, 400)} 400w, ${proxyImageUrl(item.imageLarge, `${item.id}-large`, 1200)} 1200w`
                  : undefined
              }
              sizes="(max-width: 1200px) 30vw, 20vw"
              alt={item.title}
              fetchPriority="low"
              onLoad={() => markImageLoaded(item.id)}
            />
          </div>
        </div>
        <div className="collection-card-body">
          <div className="collection-card-title">{item.title}</div>
          <div className="collection-card-meta">{item.artist}</div>
          <div className="collection-card-meta">{item.date}</div>
        </div>
      </button>
    );

    return (
      <section className="collection-panel">
        <div className="collection-search">
          <div className="collection-title">Search the collection</div>
          <div className="collection-search-row">
            <select
              className="collection-search-select"
              aria-label="Search field"
              value={searchField}
              onChange={(event) => setSearchField(event.target.value)}
            >
              {collectionSearchFields.map((field) => (
                <option key={field} value={field}>
                  {field}
                </option>
              ))}
            </select>
            <div className="search-input-wrap">
              <input
                className="search-input"
                type="search"
                placeholder={getSearchPlaceholder(searchField)}
                value={searchQuery}
                onChange={(event) => setSearchQuery(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === "Enter") {
                  setCollectionQuery(searchQuery.trim() || COLLECTION_DEFAULT_QUERY);
                  setCollectionPage(1);
                }
              }}
              />
            <button
              className="search-button"
              aria-label="Search"
              onClick={() => {
                setCollectionQuery(searchQuery.trim() || COLLECTION_DEFAULT_QUERY);
                setCollectionPage(1);
              }}
            >
                🔍
              </button>
            </div>
          </div>
        </div>

        <div className="collection-divider"></div>

        <div className="collection-meta-row">
          <div>
            {`Showing ${displayStart}-${displayEnd} of ${displayTotal.toLocaleString()} accessible artworks`}
          </div>
          <div className="collection-meta-right">
            <span>Filter by:</span>
            <select
              className="collection-select small"
              value={collectionRanking}
              onChange={(event) => {
                setCollectionRanking(event.target.value);
                setCollectionPage(1);
              }}
            >
              <option value="">Rank</option>
              <option value="unrated">Unrated</option>
              <option value="1-2">1-2 stars</option>
              <option value="2-3">2-3 stars</option>
              <option value="3-4">3-4 stars</option>
              <option value="4-5">4-5 stars</option>
            </select>
            <span>Sort by:</span>
            <select
              className="collection-select small"
              value={collectionSort}
              onChange={(event) => setCollectionSort(event.target.value)}
            >
              <option>Relevance</option>
              <option>User Ranking</option>
              <option>Random (Global)</option>
              <option>Title (a-z)</option>
              <option>Title (z-a)</option>
              <option>Date (newest-oldest)</option>
              <option>Date (oldest-newest)</option>
              <option>Artist/Maker (a-z)</option>
              <option>Artist/Maker (z-a)</option>
            </select>
          </div>
        </div>

        <section className="collection-grid">
          {collectionLoading ? (
            <div className="collection-skeletons">
              {Array.from({ length: pageSize }, (_, index) => (
                <div className="skeleton-card" key={index}>
                  <div className="skeleton-thumb"></div>
                  <div className="skeleton-line"></div>
                  <div className="skeleton-line short"></div>
                </div>
              ))}
            </div>
          ) : sortedItems.length ? (
            sortedItems.map((item) => renderCollectionCard(item))
          ) : (
            <div className="panel-text">No results found.</div>
          )}
        </section>

        <div className="collection-pagination">
          {currentPage > 1 && renderTotalPages > 1 ? (
            <button
              className="page-button"
              type="button"
              onClick={() => {
                if (collectionLoading) return;
                if (!allowNavigation(collectionNavRef, NAV_THROTTLE_MS)) return;
                setCollectionPage(currentPage - 1);
              }}
              aria-label="Previous page"
            >
              ‹
            </button>
          ) : null}
          {Array.from(
            {
              length:
                Math.min(renderTotalPages, currentPage + 2) -
                Math.max(1, currentPage - 2) +
                1
            },
            (_, index) => Math.max(1, currentPage - 2) + index
          ).map((pageNumber) => (
            <button
              key={pageNumber}
              className={`page-button${pageNumber === currentPage ? " is-active" : ""}`}
              type="button"
              onClick={() => {
                if (collectionLoading) return;
                if (!allowNavigation(collectionNavRef, NAV_THROTTLE_MS)) return;
                setCollectionPage(pageNumber);
              }}
              aria-current={pageNumber === currentPage ? "page" : undefined}
            >
              {pageNumber}
            </button>
          ))}
          {currentPage < renderTotalPages ? (
            <button
              className="page-button"
              type="button"
              onClick={() => {
                if (collectionLoading) return;
                if (!allowNavigation(collectionNavRef, NAV_THROTTLE_MS)) return;
                setCollectionPage(currentPage + 1);
              }}
              aria-label="Next page"
            >
              ›
            </button>
          ) : null}
        </div>
        <div className="collection-return">
          <button
            className="page-button page-button-wide"
            type="button"
            onClick={() => {
              setCollectionPage(1);
              window.scrollTo({ top: 0, behavior: "smooth" });
            }}
          >
            Return To Top
          </button>
        </div>
      </section>
    );
  }

  function renderAbout() {
    return (
      <section className="about-grid">
        <div>
          {aboutSection === "what-is-art" ? (
            <>
              <div className="about-title">What is art?</div>
              <div className="about-text">
                In a world where nearly everything is rated and reviewed, where we often outsource judgment to the crowd, I wondered what would happen if a known rating mechanic could be used to encourage critical thinking about something as vast, cultural, and personal as art.
              </div>
              <div className="about-text">
                I have a confession. Making this made me uncomfortable. I love art, but I sometimes struggle in art museums. The cold white walls can feel at odds with the warmth of the work. Wall texts and artist statements often feel abstracted and intellectualized, distanced from the objects they describe. I crave direct, challenging conversations about what art means. Not curator to visitor, but person to person.
              </div>
              <div className="about-text">
                This project is reductive, yes. But it mirrors the conversations I have with friends in museums. Casual, personal, and often insecure...poking at the edges of meaning and values.
              </div>
              <div className="about-text">
                This is an experiment. An attempt to wrestle with one small piece of a big question. What happens when we try to codify art? Who gets prioritized and who gets left out? Building this, I realized a high score did not always represent "good" art to me. The works that lingered with me were often the ones I rated lower. They asked more of me. That tension, that reminder of how little I know, is what I love about art. I want to be reminded of how much there is to learn. I want to be humbled and inspired.
              </div>
              <div className="about-text">
                Special thanks to <b>The Metropolitan Museum of Art</b> for making their collection data available through the <b>Open Access API</b>, helping make this exploration possible.
              </div>
            </>
          ) : null}
          {aboutSection === "why-build-this" ? (
            <>
              <div className="about-title">Why build this?</div>
              <div className="about-text">
                Frankly, this is a provocation. If you want to debate the validity of this project,
                I’ll consider it a success. I’d love to see more debate and dialogue between
                museums and their visitors. That’s healthy. That’s dynamic. And navigating
                differences to find commonalities is, I believe, a key component of communities.
              </div>
              <div className="about-title">Goals</div>
              <ul className="about-list">
                <li>To establish vocabulary for the evaluation of art that artists and non-artists
                  could understand. It’s likely the wrong criteria to someone. Maybe you. That’s
                  okay, and I hope it inspires you to consider what criteria does matter to you.
                </li>
                <li>
                  To create a tool that museum staff could use to better understand how the public
                  views their collection.
                </li>
                <li>
                  To facilitate discussion about the evaluation of art between museum visitors, providing educators with another tool for the discussion of art and design principles.
                </li>
              </ul>
            </>
          ) : null}
          {aboutSection === "evaluation-criteria" ? (
            <>
              <div className="about-title">Evaluation Criteria</div>
              <div className="about-text">
                The evaluation criteria was a hybrid of A) the language used in classrooms to teach
                the general principles of art and B) the framework for the evaluation of art in auctions.
              </div>
              <div className="criteria-block">
                <div className="criteria-title">Story</div>
                <div className="criteria-text">
                  Does this artwork convey a narrative, share a moment, or describe a scene you can imagine happening?
                </div>
              </div>
              <div className="criteria-block">
                <div className="criteria-title">Originality</div>
                <div className="criteria-text">
                  Consider the time and place this was created. Does this artwork feel innovative,
                  surprising, or different from what you usually see?
                </div>
              </div>
              <div className="criteria-block">
                <div className="criteria-title">Historical Context</div>
                <div className="criteria-text">
                  Does this artwork help you picture when, where, or how people lived?
                </div>
              </div>
              <div className="criteria-block">
                <div className="criteria-title">Visual Impact</div>
                <div className="criteria-text">
                  How strong or engaging are the visual elements like color, composition, and detail?
                </div>
              </div>
              <div className="criteria-block">
                <div className="criteria-title">Purpose</div>
                <div className="criteria-text">
                  Does this artwork seem to have a function or express a message, belief, or deeper idea?
                </div>
              </div>
              <div className="criteria-block">
                <div className="criteria-title">Overall Impression</div>
                <div className="criteria-text">
                  How much did this artwork resonate with you or stay in your mind? Did it affect you?
                  Make you feel something?
                </div>
              </div>
            </>
          ) : null}
        </div>
      </section>
    );
  }

  const mainContent = (() => {
    if (view === "details" && showSurvey) {
      return (
        <section className="survey">
          <button
            className="nav-arrow left"
            onClick={() => {
              advanceSurveyQuestion(surveyQuestion);
            }}
          >
            ‹
          </button>
          <button
            className="nav-arrow right"
            onClick={() => {
              surveyAdvanceCountRef.current += 1;
              if (surveyAdvanceCountRef.current >= 3) {
                surveyAdvanceCountRef.current = 0;
                void transitionToAvailableArtworkFromSurvey();
                return;
              }
              advanceSurveyQuestion(surveyQuestion);
            }}
          >
            ›
          </button>
          <div className="survey-body">
            <div className="survey-question">
              {surveyQuestion || surveyQuestionsState[0]}
            </div>
            <div className="survey-actions">
              <button
                className="button button-outline"
                onTouchStart={(event) => {
                  event.stopPropagation();
                  handleSurveyAnswer("no", event);
                }}
                onClick={(event) => handleSurveyAnswer("no", event)}
              >
                No
              </button>
              <button
                className="button button-outline"
                onTouchStart={(event) => {
                  event.stopPropagation();
                  handleSurveyAnswer("yes", event);
                }}
                onClick={(event) => handleSurveyAnswer("yes", event)}
              >
                Yes
              </button>
            </div>
          </div>
        </section>
      );
    }

    if (view === "compare" && ENABLE_COMPARE) {
      return renderCompare();
    }

    if (view === "questions") {
      return renderQuestions();
    }

    if (view === "results") {
      return renderResults();
    }

    if (view === "collection") {
      return renderCollection();
    }

    if (view === "about") {
      return renderAbout();
    }

    if (view === "details" && appLoading) {
      return (
        <section className="details-grid is-loading-screen">
          <div className="side-panel is-loading">
            <div className="loading-artworks" aria-label="Loading artworks">
              <div className="loading-frame"></div>
              <div className="loading-question">?</div>
              <div className="loading-label">Loading</div>
            </div>
            {appLoadError ? (
              <button className="button button-outline" onClick={() => window.location.reload()}>
                Retry
              </button>
            ) : null}
          </div>
        </section>
      );
    }

    return (
      <section
        className="details-grid"
        onTouchStart={handleSwipeStart}
        onTouchEnd={handleSwipeEnd}
      >
        <button
          className="nav-arrow left"
          onClick={() => void navigateArtwork("prev")}
        >
          ‹
        </button>
        <button
          className="nav-arrow right"
          onClick={() => void navigateArtwork("next")}
        >
          ›
        </button>
        <div className="artwork artwork-canvas">
          <div className={`image-shell ${loadedImages.has(activeArtwork.id) ? "is-loaded" : ""}`}>
            {activeArtwork.imageSmall || activeArtwork.imageLarge ? (
              <img
                src={detailImageSrc}
                srcSet={detailImageSrcSet}
                sizes="(max-width: 1200px) 90vw, 60vw"
                alt={activeArtwork.title}
                loading="eager"
                decoding="async"
                fetchPriority="high"
                onLoad={() => markImageLoaded(activeArtwork.id)}
              />
            ) : null}
          </div>
        </div>
        <div className="side-panel">
          <div className="details-meta">
            {fromCollection ? (
              <button className="back-link" onClick={() => setView("collection")}>
                &lt;&lt; Back to the Collection
              </button>
            ) : null}
            <h2 className="detail-title">{activeArtwork.title}</h2>
            <div className="detail-artist">{activeArtwork.artist}</div>
            <div className="detail-meta">{activeArtwork.culture}</div>
            <div className="detail-meta">{activeArtwork.date}</div>
            <div className="panel-text detail-description">
              {getArtworkDescription(activeArtwork)}
            </div>
          </div>
          {(!hasCommunityRatings(activeArtwork) && !ratedById.has(activeArtwork.id)) ||
          ratingModeById.has(activeArtwork.id)
            ? renderRating()
            : renderBars()}
        </div>
      </section>
    );
  })();

  return (
    <div className="app">
      <header className="top-bar">
        <button className="brand" type="button" onClick={handleHomeClick}>
          <div className="brand-mark">WHAT'S ART?</div>
        </button>
        <div className="progress-inline">
          <div className="progress-label">{progressLabel}</div>
          <div className="progress-count">
            {safeDisplayedRatingCount.toLocaleString()}
            /{safeTotalAccessible.toLocaleString()}
          </div>
        </div>
        <nav className="nav-actions">
          <button className="button button-outline" onClick={handleRateClick}>
            Rate
          </button>
          <button className="button button-outline" onClick={() => setView("collection")}>
            Collection
          </button>
          {ENABLE_COMPARE ? (
            <button className="button button-outline" onClick={() => setView("compare")}>
              Compare
            </button>
          ) : null}
          <button className="button button-outline" onClick={() => setView("questions")}>
            Questions
          </button>
          <div
            className={`nav-menu${aboutMenuOpen ? " is-open" : ""}`}
            tabIndex={0}
            onBlur={() => setAboutMenuOpen(false)}
          >
            <button
              className="nav-select"
              type="button"
              onClick={() => setAboutMenuOpen((prev) => !prev)}
            >
              About
            </button>
            <div className="nav-menu-list">
              <button
                className="nav-menu-item"
                type="button"
                onMouseDown={(event) => {
                  event.preventDefault();
                  setAboutSection("what-is-art");
                  setView("about");
                  setAboutMenuOpen(false);
                }}
              >
                What is Art?
              </button>
              <button
                className="nav-menu-item"
                type="button"
                onMouseDown={(event) => {
                  event.preventDefault();
                  setAboutSection("why-build-this");
                  setView("about");
                  setAboutMenuOpen(false);
                }}
              >
                Why build this?
              </button>
              <button
                className="nav-menu-item"
                type="button"
                onMouseDown={(event) => {
                  event.preventDefault();
                  setAboutSection("evaluation-criteria");
                  setView("about");
                  setAboutMenuOpen(false);
                }}
              >
                Evaluation Criteria
              </button>
            </div>
          </div>
        </nav>
      </header>
      {debugRatingsEnabled ? (
        <div className="ratings-debug">
          <div>Supabase: {HAS_SUPABASE ? "on" : "off"}</div>
          <div>
            Project: {debugRatingsProject ? debugRatingsProject : "—"}
          </div>
          <div>
            Service key:{" "}
            {debugRatingsHasKey === null ? "—" : debugRatingsHasKey ? "yes" : "no"}
          </div>
          <div>Anon key: {debugRatingsAnonPresent ? "yes" : "no"}</div>
          <div>
            Key role: {debugRatingsKeyRole ? debugRatingsKeyRole : "—"}
          </div>
          <div>Global status: {globalSyncStatus}</div>
          <div>
            Summary items:{" "}
            {debugRatingsCount === null ? "—" : debugRatingsCount.toLocaleString()}
          </div>
          <div>
            Updated:{" "}
            {debugRatingsAt
              ? new Date(debugRatingsAt).toLocaleTimeString()
              : "—"}
          </div>
          {debugRatingsError ? (
            <div className="ratings-debug-error">{debugRatingsError}</div>
          ) : null}
        </div>
      ) : null}

      <main className="main">{mainContent}</main>
    </div>
  );
}
